import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Calendar,
    RefreshCw,
    Download,
    Eye,
    Clock,
    AlertTriangle,
    Activity,
    ChevronLeft,
    ChevronRight,
    Search,
    CheckCircle,
    Info,
    X,
    Wind,
    Thermometer,
    Droplets,
    Filter,
    Layers,
    Cpu,
    Wifi,
    Gauge,
    Terminal,
    Send,
    Sliders,
    Code,
    Copy,
    Check,
    Table
} from 'lucide-react';

// API Base URL for Audit, Telemetry & Maintenance Commands Endpoints (taken from .env)
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');

const AUDIT_SUB_TABS = [
    '5-Minute Audit',
    'Hourly Audit',
    'Daily Audit',
    'Issues',
    'Telemetry',
    'Commands'
];

// Helper to parse JSON payload safely
const parseCommandPayload = (payload) => {
    if (!payload) return null;
    if (typeof payload === 'object') return payload;
    try {
        return JSON.parse(payload);
    } catch {
        return null;
    }
};

// Helper to flatten nested JSON payload into structured tabular rows
const flattenJsonPayload = (payload) => {
    const parsed = parseCommandPayload(payload);
    if (parsed === null || parsed === undefined) {
        if (typeof payload === 'string' && payload.trim().length > 0) {
            return [{
                key: 'raw_payload',
                path: 'raw_payload',
                type: 'string',
                value: payload,
                displayValue: payload
            }];
        }
        return [];
    }

    if (typeof parsed !== 'object') {
        return [{
            key: 'payload',
            path: 'payload',
            type: typeof parsed,
            value: parsed,
            displayValue: String(parsed)
        }];
    }

    const rows = [];

    const recurse = (current, currentPath = '') => {
        if (current === null) {
            rows.push({
                key: currentPath || 'null',
                path: currentPath,
                type: 'null',
                value: null,
                displayValue: 'null'
            });
            return;
        }

        if (Array.isArray(current)) {
            if (current.length === 0) {
                rows.push({
                    key: currentPath || '[]',
                    path: currentPath,
                    type: 'array',
                    value: [],
                    displayValue: '[]'
                });
                return;
            }
            if (current.every(x => x === null || typeof x !== 'object')) {
                rows.push({
                    key: currentPath || 'array',
                    path: currentPath,
                    type: 'array',
                    value: current,
                    displayValue: JSON.stringify(current)
                });
                return;
            }
            current.forEach((item, idx) => {
                const itemPath = currentPath ? `${currentPath}[${idx}]` : `[${idx}]`;
                recurse(item, itemPath);
            });
            return;
        }

        const keys = Object.keys(current);
        if (keys.length === 0) {
            rows.push({
                key: currentPath || '{}',
                path: currentPath,
                type: 'object',
                value: {},
                displayValue: '{}'
            });
            return;
        }

        keys.forEach(k => {
            const val = current[k];
            const nextPath = currentPath ? `${currentPath}.${k}` : k;
            if (val !== null && typeof val === 'object') {
                if (Array.isArray(val) && val.length > 0 && val.every(x => typeof x !== 'object')) {
                    rows.push({
                        key: k,
                        path: nextPath,
                        type: 'array',
                        value: val,
                        displayValue: JSON.stringify(val)
                    });
                } else if (Object.keys(val).length === 0) {
                    rows.push({
                        key: k,
                        path: nextPath,
                        type: Array.isArray(val) ? 'array' : 'object',
                        value: val,
                        displayValue: Array.isArray(val) ? '[]' : '{}'
                    });
                } else {
                    recurse(val, nextPath);
                }
            } else {
                let typeStr = typeof val;
                if (val === null) typeStr = 'null';
                rows.push({
                    key: k,
                    path: nextPath,
                    type: typeStr,
                    value: val,
                    displayValue: val === null ? 'null' : val === undefined ? 'undefined' : String(val)
                });
            }
        });
    };

    recurse(parsed);
    return rows;
};

// Helper to format command names nicely
const formatCommandName = (name) => {
    if (!name) return 'Command';
    return name
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
};

// Helper to calculate dispatch latency
const calculateLatency = (createdStr, sentStr) => {
    if (!createdStr || !sentStr) return null;
    try {
        const t1 = new Date(createdStr.includes('T') ? createdStr : createdStr.replace(' ', 'T')).getTime();
        const t2 = new Date(sentStr.includes('T') ? sentStr : sentStr.replace(' ', 'T')).getTime();
        if (isNaN(t1) || isNaN(t2)) return null;
        const diff = t2 - t1;
        return diff >= 0 ? diff : null;
    } catch {
        return null;
    }
};

// Helper to format Date into "YYYY-MM-DD HH:mm:ss"
const formatDateForApi = (date) => {
    const pad = (n) => String(n).padStart(2, '0');
    const yyyy = date.getFullYear();
    const mm = pad(date.getMonth() + 1);
    const dd = pad(date.getDate());
    const hh = pad(date.getHours());
    const min = pad(date.getMinutes());
    const ss = pad(date.getSeconds());
    return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
};

// Ensure datetime string ends with seconds
const ensureSeconds = (dateStr) => {
    if (!dateStr) return '';
    const trimmed = dateStr.trim().replace('T', ' ');
    if (trimmed.length === 16) {
        return `${trimmed}:00`;
    }
    return trimmed;
};

// Convert "YYYY-MM-DD HH:mm:ss" or "YYYY-MM-DD HH:mm" to "YYYY-MM-DDTHH:mm" for <input type="datetime-local" />
const toInputDateTime = (dtStr) => {
    if (!dtStr) return '';
    const cleaned = dtStr.trim().replace(' ', 'T');
    return cleaned.substring(0, 16);
};

// Convert "YYYY-MM-DDTHH:mm" from datetime-local input to "YYYY-MM-DD HH:mm:ss"
const fromInputDateTime = (val) => {
    if (!val) return '';
    const cleaned = val.replace('T', ' ');
    return cleaned.length === 16 ? `${cleaned}:00` : cleaned;
};

// Default Date Range: Last 24 Hours dynamically from current time
const getDefaultDateRange = () => {
    const now = new Date();
    const past24 = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    return {
        startDate: formatDateForApi(past24),
        endDate: formatDateForApi(now)
    };
};

// Static mock data removed. All data fetched dynamically from API.

// Helper to extract array safely from API response (supporting various formats)
const extractArrayData = (res) => {
    if (!res) return null;
    if (Array.isArray(res)) return res;
    if (Array.isArray(res.data)) return res.data;
    if (Array.isArray(res.results)) return res.results;
    if (res.data && Array.isArray(res.data.results)) return res.data.results;
    return null;
};

// Helper to extract object safely from API response
const extractObjectData = (res) => {
    if (!res) return null;
    if (typeof res !== 'object') return null;
    if (res.data && typeof res.data === 'object' && !Array.isArray(res.data)) return res.data;
    if (Array.isArray(res.data) && res.data.length > 0) return res.data[0];
    if (Array.isArray(res) && res.length > 0) return res[0];
    if (!res.data && !Array.isArray(res) && Object.keys(res).length > 0 && !res.detail) return res;
    return null;
};

// Helper to filter buckets by start and end timestamps
const filterBucketsByDateRange = (buckets, startStr, endStr) => {
    if (!Array.isArray(buckets) || buckets.length === 0) return [];
    if (!startStr && !endStr) return buckets;

    const parseToTime = (s) => {
        if (!s) return null;
        const normalized = s.includes('T') ? s : s.replace(' ', 'T');
        const t = new Date(normalized).getTime();
        return isNaN(t) ? null : t;
    };

    const startTime = parseToTime(startStr);
    const endTime = parseToTime(endStr);

    return buckets.filter(b => {
        const bStart = parseToTime(b.start_time);
        const bEnd = parseToTime(b.end_time) || bStart;
        if (!bStart && !bEnd) return true;

        if (startTime && bEnd && bEnd < startTime) return false;
        if (endTime && bStart && bStart > endTime) return false;
        return true;
    });
};

// Compute status distribution dynamically from 5-minute buckets
const computeStatusDistribution = (buckets) => {
    if (!buckets || buckets.length === 0) {
        return [];
    }

    const counts = {};
    buckets.forEach(b => {
        if (!b) return;
        const rawStatus = b.status || (b.efficiency_percentage != null ? (b.efficiency_percentage < 65 ? 'Watch' : 'Good') : '--');
        const s = String(rawStatus).trim();
        const normStatus = s === '--' ? '--' : (s.charAt(0).toUpperCase() + s.slice(1).toLowerCase());
        counts[normStatus] = (counts[normStatus] || 0) + 1;
    });

    const total = buckets.length;
    return Object.keys(counts).map(status => ({
        status: status,
        bucket_count: counts[status],
        bucket_percent: total > 0 ? (counts[status] / total) * 100 : 0
    }));
};

// Compute overview metrics dynamically from 5-minute buckets
const computeOverviewFromBuckets = (buckets, issues = [], poleData = null) => {
    if (!buckets || buckets.length === 0) {
        return {
            total_air_cleaned: null,
            total_air_processed: null,
            average_efficiency: null,
            average_fan_speed: null,
            uptime: null,
            average_temperature: poleData?.temperature_c != null ? Number(poleData.temperature_c) : null,
            average_humidity: poleData?.humidity_percent != null ? Number(poleData.humidity_percent) : null,
            average_pressure: null,
            five_minute_bucket_count: 0,
            issue_count: Array.isArray(issues) ? issues.length : 0
        };
    }

    const validEffBuckets = buckets.filter(b => b.efficiency_percentage != null && !isNaN(Number(b.efficiency_percentage)));
    const avgEff = validEffBuckets.length > 0
        ? validEffBuckets.reduce((sum, b) => sum + Number(b.efficiency_percentage), 0) / validEffBuckets.length
        : null;

    const validFanBuckets = buckets.filter(b => b.fan_percentage != null && !isNaN(Number(b.fan_percentage)));
    const avgFan = validFanBuckets.length > 0
        ? validFanBuckets.reduce((sum, b) => sum + Number(b.fan_percentage), 0) / validFanBuckets.length
        : null;

    const totalAirProcessed = (avgFan != null && avgEff != null)
        ? buckets.length * (avgFan / 80) * 2857.33
        : null;
    const totalAirCleaned = (totalAirProcessed != null && avgEff != null)
        ? totalAirProcessed * (avgEff / 100)
        : null;

    const goodCount = buckets.filter(b => (b.status || '').toLowerCase() === 'good').length;
    const uptime = buckets.length > 0 ? (goodCount / buckets.length) * 100 : null;

    const errorCount = buckets.filter(b => (b.status || '').toLowerCase() === 'error').length;
    const issueCount = Array.isArray(issues) && issues.length > 0 ? issues.length : errorCount;

    return {
        total_air_cleaned: totalAirCleaned,
        total_air_processed: totalAirProcessed,
        average_efficiency: avgEff,
        average_fan_speed: avgFan,
        uptime: uptime,
        average_temperature: poleData?.temperature_c != null ? Number(poleData.temperature_c) : null,
        average_humidity: poleData?.humidity_percent != null ? Number(poleData.humidity_percent) : null,
        average_pressure: null,
        five_minute_bucket_count: buckets.length,
        issue_count: issueCount
    };
};

// Group 5-minute buckets into hourly records
const aggregateHourlyFrom5Min = (fiveMinList) => {
    if (!fiveMinList || fiveMinList.length === 0) return [];

    const hourMap = {};
    fiveMinList.forEach(b => {
        if (!b.start_time) return;
        const hourKey = b.start_time.substring(0, 13);
        if (!hourMap[hourKey]) {
            hourMap[hourKey] = [];
        }
        hourMap[hourKey].push(b);
    });

    return Object.keys(hourMap).sort().reverse().map(hourKey => {
        const group = hourMap[hourKey];
        const validEff = group.filter(b => b.efficiency_percentage != null);
        const avgEff = validEff.length > 0
            ? validEff.reduce((acc, b) => acc + Number(b.efficiency_percentage), 0) / validEff.length
            : null;

        const validFan = group.filter(b => b.fan_percentage != null);
        const avgFan = validFan.length > 0
            ? validFan.reduce((acc, b) => acc + Number(b.fan_percentage), 0) / validFan.length
            : null;

        const completeCount = group.filter(b => (b.status || '').toLowerCase() === 'good').length;
        const incompleteCount = group.length - completeCount;
        const processed = (avgFan != null) ? group.length * (avgFan / 80) * 2857.33 : null;
        const cleaned = (processed != null && avgEff != null) ? processed * (avgEff / 100) : null;

        const startT = `${hourKey.replace(' ', 'T')}:00:00`;
        const hourParts = hourKey.split(/[ T]/);
        const hourNum = parseInt(hourParts[1] || '0', 10);
        const nextHourStr = String(Math.min(23, hourNum + 1)).padStart(2, '0');
        const endT = `${hourParts[0]}T${nextHourStr}:00:00`;

        return {
            start_time: startT,
            end_time: endT,
            five_minute_bucket_count: group.length,
            complete_bucket_count: completeCount,
            incomplete_bucket_count: incompleteCount,
            sample_count: group.length * 5,
            average_efficiency_percentage: avgEff,
            effective_efficiency_percentage: avgEff != null && group.length > 0 ? avgEff * (completeCount / group.length) : null,
            fan_percentage: avgFan,
            total_air_processed: processed,
            total_air_cleaned: cleaned,
            status: group.length === 0 ? '--' : (incompleteCount > 0 ? (incompleteCount > 2 ? 'Error' : 'Watch') : 'Good')
        };
    });
};

// Group hourly records into daily records
const aggregateDailyFromHourly = (hourlyList) => {
    if (!hourlyList || hourlyList.length === 0) return [];

    const dayMap = {};
    hourlyList.forEach(h => {
        if (!h.start_time) return;
        const dayKey = h.start_time.substring(0, 10);
        if (!dayMap[dayKey]) {
            dayMap[dayKey] = [];
        }
        dayMap[dayKey].push(h);
    });

    return Object.keys(dayMap).sort().reverse().map(dayKey => {
        const group = dayMap[dayKey];
        const total5Min = group.reduce((acc, h) => acc + (Number(h.five_minute_bucket_count) || 0), 0);
        const complete5Min = group.reduce((acc, h) => acc + (Number(h.complete_bucket_count) || 0), 0);
        const incomplete5Min = group.reduce((acc, h) => acc + (Number(h.incomplete_bucket_count) || 0), 0);
        const totalSamples = group.reduce((acc, h) => acc + (Number(h.sample_count) || 0), 0);
        const totalProcessed = group.reduce((acc, h) => acc + (Number(h.total_air_processed) || 0), 0);
        const totalCleaned = group.reduce((acc, h) => acc + (Number(h.total_air_cleaned) || 0), 0);

        const validEff = group.filter(h => h.average_efficiency_percentage != null);
        const avgEff = validEff.length > 0
            ? validEff.reduce((acc, h) => acc + Number(h.average_efficiency_percentage), 0) / validEff.length
            : null;

        const validFan = group.filter(h => h.fan_percentage != null);
        const avgFan = validFan.length > 0
            ? validFan.reduce((acc, h) => acc + Number(h.fan_percentage), 0) / validFan.length
            : null;

        return {
            operational_date: dayKey,
            start_time: `${dayKey} 00:00:00`,
            end_time: `${dayKey} 23:59:59`,
            five_minute_bucket_count: total5Min,
            complete_bucket_count: complete5Min,
            incomplete_bucket_count: incomplete5Min,
            sample_count: totalSamples,
            average_efficiency_percentage: avgEff,
            effective_efficiency_percentage: avgEff != null && total5Min > 0 ? avgEff * (complete5Min / total5Min) : null,
            fan_percentage: avgFan,
            total_air_processed: totalProcessed > 0 ? totalProcessed : null,
            total_air_cleaned: totalCleaned > 0 ? totalCleaned : null,
            status: total5Min === 0 ? '--' : (incomplete5Min > 3 ? 'Error' : (incomplete5Min > 0 ? 'Watch' : 'Good'))
        };
    });
};

export const MaintenanceAuditPanel = ({ pole, renderId, onShowToast }) => {
    const poleId = renderId || pole?.poleNumber || pole?.pole_id || pole?.id || pole?.name || '';

    // 5-Minute Audit is the active tab by default
    const [activeSubTab, setActiveSubTab] = useState('5-Minute Audit');
    const [timeBasis, setTimeBasis] = useState('Local');

    // Date range filter
    const initialRange = getDefaultDateRange();
    const [startDate, setStartDate] = useState(initialRange.startDate);
    const [endDate, setEndDate] = useState(initialRange.endDate);
    const [activePreset, setActivePreset] = useState('last24h');

    // In-panel alert feedback state
    const [panelAlert, setPanelAlert] = useState(null);
    const alertTimeoutRef = useRef(null);

    const triggerAlert = useCallback((type, message) => {
        if (onShowToast) {
            onShowToast({ type, message });
        }
        if (alertTimeoutRef.current) {
            clearTimeout(alertTimeoutRef.current);
        }
        setPanelAlert({ type, message, id: Date.now() });
        alertTimeoutRef.current = setTimeout(() => {
            setPanelAlert(null);
        }, 3500);
    }, [onShowToast]);

    useEffect(() => {
        return () => {
            if (alertTimeoutRef.current) {
                clearTimeout(alertTimeoutRef.current);
            }
        };
    }, []);

    // Input refs to trigger native calendar popups
    const startInputRef = useRef(null);
    const endInputRef = useRef(null);

    // Loading & Refresh states
    const [loading, setLoading] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Selected Detail Modal
    const [selectedDetail, setSelectedDetail] = useState(null);
    const [bucketTelemetryList, setBucketTelemetryList] = useState([]);
    const [bucketTelemetryLoading, setBucketTelemetryLoading] = useState(false);
    const [bucketTelemetryError, setBucketTelemetryError] = useState(null);
    const [expandedTelemetryId, setExpandedTelemetryId] = useState(null);

    // ─── DATA STATES (Dynamic API-Driven, No Mock Data) ──────
    // 1. Overview data
    const [overviewData, setOverviewData] = useState(null);

    // 2. 5-Minute Audit Data
    const [fiveMinBuckets, setFiveMinBuckets] = useState([]);
    const [fiveMinStatusDist, setFiveMinStatusDist] = useState([]);
    const [fiveMinPage, setFiveMinPage] = useState(1);
    const pageSize5Min = 10;

    // 3. Issues Data
    const [topIssueTypes, setTopIssueTypes] = useState([]);
    const [recentIssues, setRecentIssues] = useState([]);
    const [recentIssuesPage, setRecentIssuesPage] = useState(1);
    const pageSizeRecentIssues = 10;

    // 4. Hourly Audit Data
    const [hourlyBuckets, setHourlyBuckets] = useState([]);
    const [hourlyStatusDist, setHourlyStatusDist] = useState([]);
    const [hourlyPage, setHourlyPage] = useState(1);
    const pageSizeHourly = 10;

    // 5. Daily Audit Data
    const [dailyBuckets, setDailyBuckets] = useState([]);
    const [dailyStatusDist, setDailyStatusDist] = useState([]);
    const [dailyPage, setDailyPage] = useState(1);
    const pageSizeDaily = 10;

    // 6. 24-Hour Telemetry Stream Data
    const [telemetryRecords, setTelemetryRecords] = useState([]);
    const [telemetryLoading, setTelemetryLoading] = useState(false);
    const [telemetryError, setTelemetryError] = useState(null);
    const [telemetryPage, setTelemetryPage] = useState(1);
    const [telemetryPageSize, setTelemetryPageSize] = useState(20);
    const [telemetrySearch, setTelemetrySearch] = useState('');
    const [telemetryCategory, setTelemetryCategory] = useState('all'); // 'all', 'pm', 'env', 'particles', 'diagnostics'
    const [expandedMainTelemetryId, setExpandedMainTelemetryId] = useState(null);

    // 7. Maintenance Commands Data
    const [commandRecords, setCommandRecords] = useState([]);
    const [commandsLoading, setCommandsLoading] = useState(false);
    const [commandsError, setCommandsError] = useState(null);
    const [commandsPage, setCommandsPage] = useState(1);
    const [commandsPageSize, setCommandsPageSize] = useState(20);
    const [commandsSearch, setCommandsSearch] = useState('');
    const [commandsActionFilter, setCommandsActionFilter] = useState('all');
    const [expandedCommandId, setExpandedCommandId] = useState(null);
    const [copiedCommandId, setCopiedCommandId] = useState(null);
    const [payloadViewMode, setPayloadViewMode] = useState('table'); // 'table' | 'raw'
    const [payloadSearch, setPayloadSearch] = useState('');

    // ─── DATE PICKER OPEN HANDLERS ────────────────────────────
    const handleOpenStartPicker = () => {
        if (startInputRef.current) {
            if (typeof startInputRef.current.showPicker === 'function') {
                startInputRef.current.showPicker();
            } else {
                startInputRef.current.focus();
            }
        }
    };

    const handleOpenEndPicker = () => {
        if (endInputRef.current) {
            if (typeof endInputRef.current.showPicker === 'function') {
                endInputRef.current.showPicker();
            } else {
                endInputRef.current.focus();
            }
        }
    };

    // ─── API FETCH WRAPPER WITH AUTH & SAFE ENCODING ───────────
    const fetchApiData = async (endpoint, params = {}, baseUrl = API_BASE_URL) => {
        try {
            const token = localStorage.getItem('accessToken');
            const cleanEndpoint = endpoint.replace(/^\/+/, '');

            const queryString = Object.keys(params)
                .filter(key => params[key] !== undefined && params[key] !== null && params[key] !== '')
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
                .join('&');

            const url = `${baseUrl}/${cleanEndpoint}${queryString ? '?' + queryString : ''}`;

            let response = null;

            // 1. Try with Authorization token if available
            try {
                const headers = { 'Content-Type': 'application/json' };
                if (token) headers['Authorization'] = `Bearer ${token}`;
                response = await fetch(url, { method: 'GET', headers });
            } catch (netErr) {
                // If fetch threw (e.g. CORS preflight blocked on Authorization header across domains), retry without auth header
                console.warn(`[MaintenanceAudit] Fetch with auth failed for ${url}, retrying without Authorization header:`, netErr);
                try {
                    response = await fetch(url, { method: 'GET' });
                } catch (netErr2) {
                    console.warn(`[MaintenanceAudit] Fetch without auth also failed for ${url}:`, netErr2);
                    return null;
                }
            }

            // 2. If 401/403 returned with token, retry without token
            if (response && (response.status === 401 || response.status === 403) && token) {
                try {
                    response = await fetch(url, { method: 'GET' });
                } catch {
                    return null;
                }
            }

            if (!response || !response.ok) {
                console.warn(`[MaintenanceAudit] API ${url} returned status: ${response?.status}`);
                return null;
            }

            const json = await response.json();
            return json;
        } catch (error) {
            console.error(`[MaintenanceAudit] Error requesting ${endpoint}:`, error);
            return null;
        }
    };

    // ─── LOAD AUDIT DATA BASED ON FILTERS ─────────────────────
    const loadAuditData = useCallback(async (isUserRefresh = false, overrideStart = null, overrideEnd = null) => {
        if (!poleId) return;

        const effectiveStart = overrideStart || startDate;
        const effectiveEnd = overrideEnd || endDate;

        if (isUserRefresh) {
            setIsRefreshing(true);
        } else {
            setLoading(true);
        }

        const queryParams = {
            pole_id: poleId,
            start_date: ensureSeconds(effectiveStart),
            end_date: ensureSeconds(effectiveEnd)
        };

        try {
            // Fetch All Related Endpoints in parallel with filtered dates
            const [
                overviewResDirect,
                fiveMinRes,
                fiveMinDistRes,
                issueTypesRes,
                recentIssuesRes,
                hourlyRes,
                hourlyDistRes,
                dailyRes,
                dailyDistRes
            ] = await Promise.all([
                fetchApiData('read-pole-audit-overview/', queryParams)
                    .then(res => res || fetchApiData('read_pole_audit_overview/', queryParams)),
                fetchApiData('read_pole_audit_5_minute/', queryParams),
                fetchApiData('read_pole_audit_5_minute_status_distribution/', queryParams),
                fetchApiData('read_pole_audit_issue_types/', queryParams),
                fetchApiData('read_pole_audit_recent_issues/', queryParams),
                fetchApiData('read_pole_audit_hourly/', queryParams),
                fetchApiData('read_pole_audit_hourly_status_distribution/', queryParams),
                fetchApiData('read_pole_audit_daily/', queryParams),
                fetchApiData('read_pole_audit_daily_status_distribution/', queryParams)
            ]);

            // 1. Process 5-Minute Buckets from API
            const raw5Min = extractArrayData(fiveMinRes);
            const resolved5Min = (raw5Min && Array.isArray(raw5Min)) ? raw5Min : [];
            setFiveMinBuckets(resolved5Min);
            setFiveMinPage(1);

            // 2. Process Issues Data from API
            const rawTopIssues = extractArrayData(issueTypesRes) || [];
            const rawRecentIssues = extractArrayData(recentIssuesRes) || [];
            setTopIssueTypes(rawTopIssues);
            setRecentIssues(rawRecentIssues);
            setRecentIssuesPage(1);

            // 3. Process Overview Data from API
            const rawOverview = extractObjectData(overviewResDirect);
            const resolvedOverview = (rawOverview && (rawOverview.total_air_cleaned != null || rawOverview.five_minute_bucket_count != null))
                ? rawOverview
                : (resolved5Min.length > 0 ? computeOverviewFromBuckets(resolved5Min, rawRecentIssues, pole) : null);
            setOverviewData(resolvedOverview);

            // 4. Process 5-Minute Status Distribution from API
            const raw5MinDist = extractArrayData(fiveMinDistRes);
            const resolved5MinDist = (raw5MinDist && raw5MinDist.length > 0)
                ? raw5MinDist
                : (resolved5Min.length > 0 ? computeStatusDistribution(resolved5Min) : []);
            setFiveMinStatusDist(resolved5MinDist);

            // 5. Process Hourly Data from API
            const rawHourly = extractArrayData(hourlyRes);
            const resolvedHourly = (rawHourly && rawHourly.length > 0)
                ? rawHourly
                : (resolved5Min.length > 0 ? aggregateHourlyFrom5Min(resolved5Min) : []);
            setHourlyBuckets(resolvedHourly);
            setHourlyPage(1);

            const rawHourlyDist = extractArrayData(hourlyDistRes);
            const resolvedHourlyDist = (rawHourlyDist && rawHourlyDist.length > 0)
                ? rawHourlyDist
                : (resolvedHourly.length > 0 ? computeStatusDistribution(resolvedHourly) : []);
            setHourlyStatusDist(resolvedHourlyDist);

            // 6. Process Daily Data from API
            const rawDaily = extractArrayData(dailyRes);
            const resolvedDaily = (rawDaily && rawDaily.length > 0)
                ? rawDaily
                : (resolvedHourly.length > 0 ? aggregateDailyFromHourly(resolvedHourly) : []);
            setDailyBuckets(resolvedDaily);
            setDailyPage(1);

            const rawDailyDist = extractArrayData(dailyDistRes);
            const resolvedDailyDist = (rawDailyDist && rawDailyDist.length > 0)
                ? rawDailyDist
                : (resolvedDaily.length > 0 ? computeStatusDistribution(resolvedDaily) : []);
            setDailyStatusDist(resolvedDailyDist);

            if (isUserRefresh) {
                triggerAlert('success', `Audit data refreshed for Pole ${poleId}`);
            }
        } catch (err) {
            console.error('Failed to load audit data:', err);
            setFiveMinBuckets([]);
            setFiveMinStatusDist([]);
            setFiveMinPage(1);
            setOverviewData(null);
            setTopIssueTypes([]);
            setRecentIssues([]);
            setRecentIssuesPage(1);
            setHourlyBuckets([]);
            setHourlyStatusDist([]);
            setHourlyPage(1);
            setDailyBuckets([]);
            setDailyStatusDist([]);
            setDailyPage(1);

            triggerAlert('error', 'Failed to fetch audit data from server');
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    }, [poleId, startDate, endDate, pole, triggerAlert]);

    // ─── LOAD 24H TELEMETRY STREAM DATA ───────────────────────
    const loadTelemetryData = useCallback(async (isUserRefresh = false, overrideStart = null, overrideEnd = null) => {
        if (!poleId) return;

        const effectiveStart = overrideStart || startDate;
        const effectiveEnd = overrideEnd || endDate;
        const dateBasis = (timeBasis === 'UTC' || timeBasis === 'utc') ? 'utc' : 'operational';

        setTelemetryLoading(true);
        setTelemetryError(null);

        const queryParams = {
            pole_id: poleId,
            start_date: ensureSeconds(effectiveStart),
            end_date: ensureSeconds(effectiveEnd),
            date_basis: dateBasis
        };

        try {
            console.log(`[MaintenanceAudit] Loading 24h telemetry for pole=${poleId}, start=${queryParams.start_date}, end=${queryParams.end_date}, date_basis=${dateBasis}`);

            // 1. Try primary endpoint: maintenance-telemetry/
            let res = await fetchApiData('maintenance-telemetry/', queryParams);

            // 2. Try without trailing slash: maintenance-telemetry
            if (!res) {
                res = await fetchApiData('maintenance-telemetry', queryParams);
            }

            // 3. Try underscore variant: maintenance_telemetry/
            if (!res) {
                res = await fetchApiData('maintenance_telemetry/', queryParams);
            }

            // 4. Try underscore without slash: maintenance_telemetry
            if (!res) {
                res = await fetchApiData('maintenance_telemetry', queryParams);
            }

            if (res) {
                const dataArr = extractArrayData(res) || [];
                setTelemetryRecords(dataArr);
                setTelemetryPage(1);
                if (dataArr.length === 0) {
                    console.log(`[MaintenanceAudit] 0 telemetry records found for Pole ${poleId}`);
                } else if (isUserRefresh) {
                    triggerAlert('success', `Fetched ${dataArr.length} telemetry records for Pole ${poleId}`);
                }
            } else {
                setTelemetryRecords([]);
                setTelemetryError(`Unable to load telemetry records from server.`);
            }
        } catch (err) {
            console.error('[MaintenanceAudit] Error loading telemetry data:', err);
            setTelemetryRecords([]);
            setTelemetryError('Failed to fetch telemetry records from server.');
        } finally {
            setTelemetryLoading(false);
        }
    }, [poleId, startDate, endDate, timeBasis, triggerAlert]);

    // ─── LOAD MAINTENANCE COMMANDS DATA ───────────────────────
    const loadCommandsData = useCallback(async (isUserRefresh = false, overrideStart = null, overrideEnd = null) => {
        if (!poleId) return;

        const effectiveStart = overrideStart || startDate;
        const effectiveEnd = overrideEnd || endDate;
        const dateBasis = (timeBasis === 'UTC' || timeBasis === 'utc') ? 'utc' : 'operational';

        setCommandsLoading(true);
        setCommandsError(null);

        const queryParams = {
            pole_id: poleId,
            start_date: ensureSeconds(effectiveStart),
            end_date: ensureSeconds(effectiveEnd),
            date_basis: dateBasis
        };

        try {
            console.log(`[MaintenanceAudit] Loading commands for pole=${poleId}, start=${queryParams.start_date}, end=${queryParams.end_date}, date_basis=${dateBasis}`);

            // 1. Try maintenance-commands
            let res = await fetchApiData('maintenance-commands', queryParams);

            // 2. Try with trailing slash: maintenance-commands/
            if (!res) {
                res = await fetchApiData('maintenance-commands/', queryParams);
            }

            // 3. Try underscore variant: maintenance_commands/
            if (!res) {
                res = await fetchApiData('maintenance_commands/', queryParams);
            }

            // 4. Try underscore variant: maintenance_commands
            if (!res) {
                res = await fetchApiData('maintenance_commands', queryParams);
            }

            if (res) {
                const dataArr = extractArrayData(res) || [];
                setCommandRecords(dataArr);
                setCommandsPage(1);
                if (dataArr.length === 0) {
                    console.log(`[MaintenanceAudit] 0 command records found for Pole ${poleId} in range ${queryParams.start_date} to ${queryParams.end_date}`);
                } else if (isUserRefresh) {
                    triggerAlert('success', `Fetched ${dataArr.length} maintenance command records for Pole ${poleId}`);
                }
            } else {
                setCommandRecords([]);
                setCommandsError(`Unable to load maintenance commands from server.`);
            }
        } catch (err) {
            console.error('[MaintenanceAudit] Error loading maintenance commands data:', err);
            setCommandRecords([]);
            setCommandsError('Failed to fetch maintenance commands from server.');
        } finally {
            setCommandsLoading(false);
        }
    }, [poleId, startDate, endDate, timeBasis, triggerAlert]);

    // Initial Load & On Pole Change
    useEffect(() => {
        loadAuditData(false);
        loadTelemetryData(false);
        loadCommandsData(false);
    }, [poleId]); // Load whenever poleId changes

    // Sub-tab switch tracker: Only reload when user actually clicks to switch sub-tabs
    const prevSubTabRef = useRef(activeSubTab);
    useEffect(() => {
        if (prevSubTabRef.current !== activeSubTab) {
            prevSubTabRef.current = activeSubTab;
            if (activeSubTab === 'Telemetry') {
                loadTelemetryData(false);
            } else if (activeSubTab === 'Commands') {
                loadCommandsData(false);
            }
        }
    }, [activeSubTab, loadTelemetryData, loadCommandsData]);

    const handleSearch = () => {
        setActivePreset(null);
        triggerAlert('info', `Searching audit records for ${startDate} to ${endDate}...`);
        loadAuditData(false, startDate, endDate);
        loadTelemetryData(false, startDate, endDate);
        loadCommandsData(false, startDate, endDate);
    };

    const handleRefresh = () => {
        triggerAlert('info', `Refreshing audit data for Pole ${poleId}...`);
        loadAuditData(true, startDate, endDate);
        loadTelemetryData(true, startDate, endDate);
        loadCommandsData(true, startDate, endDate);
    };

    // Preset Date Filter Handlers
    const setTodayPreset = () => {
        const now = new Date();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        const s = formatDateForApi(todayStart);
        const e = formatDateForApi(now);
        setActivePreset('today');
        setStartDate(s);
        setEndDate(e);
        triggerAlert('success', 'Showing audit data for Today (00:00 to now)');
        loadAuditData(false, s, e);
        loadTelemetryData(false, s, e);
        loadCommandsData(false, s, e);
    };

    const setLast24HoursPreset = () => {
        const now = new Date();
        const past24 = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        const s = formatDateForApi(past24);
        const e = formatDateForApi(now);
        setActivePreset('last24h');
        setStartDate(s);
        setEndDate(e);
        triggerAlert('success', 'Showing audit data for Last 24 Hours');
        loadAuditData(false, s, e);
        loadTelemetryData(false, s, e);
        loadCommandsData(false, s, e);
    };

    const setLast7DaysPreset = () => {
        const now = new Date();
        const past7d = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const s = formatDateForApi(past7d);
        const e = formatDateForApi(now);
        setActivePreset('last7d');
        setStartDate(s);
        setEndDate(e);
        triggerAlert('success', 'Showing audit data for Last 7 Days');
        loadAuditData(false, s, e);
        loadTelemetryData(false, s, e);
        loadCommandsData(false, s, e);
    };

    const setLast30DaysPreset = () => {
        const now = new Date();
        const past30d = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        const s = formatDateForApi(past30d);
        const e = formatDateForApi(now);
        setActivePreset('last30d');
        setStartDate(s);
        setEndDate(e);
        triggerAlert('success', 'Showing audit data for Last 30 Days');
        loadAuditData(false, s, e);
        loadTelemetryData(false, s, e);
        loadCommandsData(false, s, e);
    };

    // ─── CSV EXPORT ───────────────────────────────────────────
    const handleExportCSV = () => {
        let headers = [];
        let rows = [];
        let filename = `${poleId}_audit.csv`;

        if (activeSubTab === '5-Minute Audit') {
            headers = ['Start Time', 'End Time', 'PM10 Inlet', 'PM10 Outlet', 'PM2.5 Inlet', 'PM2.5 Outlet', 'Efficiency %', 'Fan %', 'Status'];
            rows = fiveMinBuckets.map(b => [
                `"${b.start_time || ''}"`,
                `"${b.end_time || ''}"`,
                `"${b.pm10_inlet != null ? b.pm10_inlet : ''}"`,
                `"${b.pm10_outlet != null ? b.pm10_outlet : ''}"`,
                `"${b.pm25_inlet != null ? b.pm25_inlet : ''}"`,
                `"${b.pm25_outlet != null ? b.pm25_outlet : ''}"`,
                `"${b.efficiency_percentage != null ? Number(b.efficiency_percentage).toFixed(2) : ''}"`,
                `"${b.fan_percentage != null ? b.fan_percentage : ''}"`,
                `"${b.status || ''}"`
            ]);
            filename = `${poleId}_5min_audit_${startDate.replace(/[: ]/g, '_')}.csv`;
        } else if (activeSubTab === 'Hourly Audit') {
            headers = ['Start Time', 'End Time', '5-Min Buckets', 'Complete', 'Incomplete', 'Samples', 'Avg Eff. %', 'Effective Eff. %', 'Fan %', 'Air Processed (cu ft)', 'Air Cleaned (cu ft)', 'Status'];
            rows = hourlyBuckets.map(h => [
                `"${h.start_time || ''}"`,
                `"${h.end_time || ''}"`,
                `"${h.five_minute_bucket_count || ''}"`,
                `"${h.complete_bucket_count || ''}"`,
                `"${h.incomplete_bucket_count || ''}"`,
                `"${h.sample_count || ''}"`,
                `"${h.average_efficiency_percentage != null ? Number(h.average_efficiency_percentage).toFixed(2) : ''}"`,
                `"${h.effective_efficiency_percentage != null ? Number(h.effective_efficiency_percentage).toFixed(2) : ''}"`,
                `"${h.fan_percentage != null ? h.fan_percentage : ''}"`,
                `"${h.total_air_processed != null ? h.total_air_processed : ''}"`,
                `"${h.total_air_cleaned != null ? h.total_air_cleaned : ''}"`,
                `"${h.status || ''}"`
            ]);
            filename = `${poleId}_hourly_audit_${startDate.replace(/[: ]/g, '_')}.csv`;
        } else if (activeSubTab === 'Daily Audit') {
            headers = ['Date', 'Start Time', 'End Time', '5-Min Buckets', 'Complete', 'Incomplete', 'Samples', 'Avg Eff. %', 'Effective Eff. %', 'Fan %', 'Total Air Processed', 'Total Air Cleaned', 'Status'];
            rows = dailyBuckets.map(d => [
                `"${d.operational_date || ''}"`,
                `"${d.start_time || ''}"`,
                `"${d.end_time || ''}"`,
                `"${d.five_minute_bucket_count || ''}"`,
                `"${d.complete_bucket_count || ''}"`,
                `"${d.incomplete_bucket_count || ''}"`,
                `"${d.sample_count || ''}"`,
                `"${d.average_efficiency_percentage != null ? Number(d.average_efficiency_percentage).toFixed(2) : ''}"`,
                `"${d.effective_efficiency_percentage != null ? Number(d.effective_efficiency_percentage).toFixed(2) : ''}"`,
                `"${d.fan_percentage != null ? d.fan_percentage : ''}"`,
                `"${d.total_air_processed != null ? d.total_air_processed : ''}"`,
                `"${d.total_air_cleaned != null ? d.total_air_cleaned : ''}"`,
                `"${d.status || ''}"`
            ]);
            filename = `${poleId}_daily_audit_${startDate.replace(/[: ]/g, '_')}.csv`;
        } else if (activeSubTab === 'Telemetry') {
            headers = [
                'Sensor Telemetry ID',
                'MQTT Message ID',
                'Received Datetime',
                'Received UTC',
                'Created UTC',
                'Device ID',
                'Topic',
                'Inlet PM1.0 (ug/m3)',
                'Outlet PM1.0 (ug/m3)',
                'Inlet PM2.5 (ug/m3)',
                'Outlet PM2.5 (ug/m3)',
                'Inlet PM4.0 (ug/m3)',
                'Outlet PM4.0 (ug/m3)',
                'Inlet PM10 (ug/m3)',
                'Outlet PM10 (ug/m3)',
                'PM2.5 Removal Eff %',
                'Inlet Temp (C)',
                'Outlet Temp (C)',
                'Inlet Humidity (%)',
                'Outlet Humidity (%)',
                'Inlet CO2 (ppm)',
                'Outlet CO2 (ppm)',
                'Inlet Particle Count 0.5',
                'Outlet Particle Count 0.5',
                'Inlet Particle Count 1.0',
                'Outlet Particle Count 1.0',
                'Inlet Particle Count 2.5',
                'Outlet Particle Count 2.5',
                'Inlet Particle Count 4.0',
                'Outlet Particle Count 4.0',
                'Inlet Particle Count 10',
                'Outlet Particle Count 10',
                'Pressure (Pa)',
                'Device Health',
                'Device State',
                'Signal (dBm)',
                'Publish Interval (s)',
                'Fan Mode',
                'Fan Speed In (%)',
                'Fan Speed Out (%)'
            ];
            rows = telemetryRecords.map(t => {
                const pm25Eff = (t.inlet_pm2_5_ug_m3 != null && t.outlet_pm2_5_ug_m3 != null && t.inlet_pm2_5_ug_m3 > 0)
                    ? (((t.inlet_pm2_5_ug_m3 - t.outlet_pm2_5_ug_m3) / t.inlet_pm2_5_ug_m3) * 100).toFixed(2)
                    : '';
                return [
                    `"${t.sensor_telemetry_id ?? ''}"`,
                    `"${t.mqtt_message_id ?? ''}"`,
                    `"${t.received_datetime ?? ''}"`,
                    `"${t.received_utc ?? ''}"`,
                    `"${t.created_utc ?? ''}"`,
                    `"${t.device_id ?? poleId}"`,
                    `"${t.topic ?? ''}"`,
                    `"${t.inlet_pm1_0_ug_m3 != null ? t.inlet_pm1_0_ug_m3 : ''}"`,
                    `"${t.outlet_pm1_0_ug_m3 != null ? t.outlet_pm1_0_ug_m3 : ''}"`,
                    `"${t.inlet_pm2_5_ug_m3 != null ? t.inlet_pm2_5_ug_m3 : ''}"`,
                    `"${t.outlet_pm2_5_ug_m3 != null ? t.outlet_pm2_5_ug_m3 : ''}"`,
                    `"${t.inlet_pm4_0_ug_m3 != null ? t.inlet_pm4_0_ug_m3 : ''}"`,
                    `"${t.outlet_pm4_0_ug_m3 != null ? t.outlet_pm4_0_ug_m3 : ''}"`,
                    `"${t.inlet_pm10_ug_m3 != null ? t.inlet_pm10_ug_m3 : ''}"`,
                    `"${t.outlet_pm10_ug_m3 != null ? t.outlet_pm10_ug_m3 : ''}"`,
                    `"${pm25Eff}"`,
                    `"${t.inlet_temperature_c != null ? t.inlet_temperature_c : ''}"`,
                    `"${t.outlet_temperature_c != null ? t.outlet_temperature_c : ''}"`,
                    `"${t.inlet_humidity_percent != null ? t.inlet_humidity_percent : ''}"`,
                    `"${t.outlet_humidity_percent != null ? t.outlet_humidity_percent : ''}"`,
                    `"${t.inlet_co2_ppm != null ? t.inlet_co2_ppm : ''}"`,
                    `"${t.outlet_co2_ppm != null ? t.outlet_co2_ppm : ''}"`,
                    `"${t.inlet_particlecount_0_5 != null ? t.inlet_particlecount_0_5 : ''}"`,
                    `"${t.outlet_particlecount_0_5 != null ? t.outlet_particlecount_0_5 : ''}"`,
                    `"${t.inlet_particlecount_1_0 != null ? t.inlet_particlecount_1_0 : ''}"`,
                    `"${t.outlet_particlecount_1_0 != null ? t.outlet_particlecount_1_0 : ''}"`,
                    `"${t.inlet_particlecount_2_5 != null ? t.inlet_particlecount_2_5 : ''}"`,
                    `"${t.outlet_particlecount_2_5 != null ? t.outlet_particlecount_2_5 : ''}"`,
                    `"${t.inlet_particlecount_4_0 != null ? t.inlet_particlecount_4_0 : ''}"`,
                    `"${t.outlet_particlecount_4_0 != null ? t.outlet_particlecount_4_0 : ''}"`,
                    `"${t.inlet_particlecount_10 != null ? t.inlet_particlecount_10 : ''}"`,
                    `"${t.outlet_particlecount_10 != null ? t.outlet_particlecount_10 : ''}"`,
                    `"${t.pressure_pa != null ? t.pressure_pa : ''}"`,
                    `"${t.device_health ?? ''}"`,
                    `"${t.device_state ?? ''}"`,
                    `"${t.signal_strength_dbm != null ? t.signal_strength_dbm : ''}"`,
                    `"${t.publish_interval_sec != null ? t.publish_interval_sec : ''}"`,
                    `"${t.fan_mode ?? ''}"`,
                    `"${t.fan_speed_in_percent != null ? t.fan_speed_in_percent : (t.fan_percentage ?? '')}"`,
                    `"${t.fan_speed_out_percent != null ? t.fan_speed_out_percent : ''}"`
                ];
            });
            filename = `${poleId}_telemetry_24h_${startDate.replace(/[: ]/g, '_')}.csv`;
        } else if (activeSubTab === 'Issues') {
            headers = ['Bucket Window', 'Issue Name / Code', 'Severity', 'Message', 'Last Occurrence'];
            rows = recentIssues.map(issue => {
                const times = getIssueTimes(issue);
                const sev = getIssueSeverity(issue);
                const msg = getIssueMessage(issue);
                return [
                    `"${times.start} - ${times.end}"`,
                    `"${formatIssueTypeName(issue.issue_type || issue.type)}"`,
                    `"${sev}"`,
                    `"${(msg || '').replace(/"/g, '""')}"`,
                    `"${issue.last_occurrence_datetime || ''}"`
                ];
            });
            filename = `${poleId}_issues_${startDate.replace(/[: ]/g, '_')}.csv`;
        } else if (activeSubTab === 'Commands') {
            headers = [
                'Command ID / Index',
                'Command Name',
                'Action',
                'Created Datetime',
                'Sent Datetime',
                'Latency (ms)',
                'Mode',
                'Fan Speed In (%)',
                'Fan Speed Out (%)',
                'Payload JSON'
            ];
            rows = commandRecords.map((cmd, idx) => {
                const parsed = parseCommandPayload(cmd.payload_json);
                const cmdObj = parsed?.command || parsed || {};
                const latency = calculateLatency(cmd.created_datetime, cmd.sent_datetime);
                return [
                    `"${idx + 1}"`,
                    `"${cmd.command_name || ''}"`,
                    `"${cmd.action || ''}"`,
                    `"${cmd.created_datetime || ''}"`,
                    `"${cmd.sent_datetime || ''}"`,
                    `"${latency != null ? latency : ''}"`,
                    `"${cmdObj.mode || ''}"`,
                    `"${cmdObj.fan_speed_in_percent != null ? cmdObj.fan_speed_in_percent : ''}"`,
                    `"${cmdObj.fan_speed_out_percent != null ? cmdObj.fan_speed_out_percent : ''}"`,
                    `"${(typeof cmd.payload_json === 'string' ? cmd.payload_json : JSON.stringify(cmd.payload_json || '')).replace(/"/g, '""')}"`
                ];
            });
            filename = `${poleId}_commands_${startDate.replace(/[: ]/g, '_')}.csv`;
        }

        const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        triggerAlert('success', `Exported ${activeSubTab} CSV successfully!`);
    };

    // Copy JSON payload with alert and visual feedback
    const handleCopyPayloadJson = (text, id) => {
        if (!text) return;
        try {
            const formatted = typeof text === 'string'
                ? (() => {
                    try { return JSON.stringify(JSON.parse(text), null, 2); } catch { return text; }
                })()
                : JSON.stringify(text, null, 2);
            navigator.clipboard.writeText(formatted);
            setCopiedCommandId(id);
            triggerAlert('success', 'Command JSON payload copied to clipboard!');
            setTimeout(() => setCopiedCommandId(null), 2500);
        } catch {
            triggerAlert('error', 'Failed to copy JSON to clipboard');
        }
    };

    // ─── HELPERS ──────────────────────────────────────────────
    const formatTimeDisplay = (dateStr) => {
        if (!dateStr) return '--';
        try {
            const normalized = String(dateStr).trim();
            // If already just HH:mm or HH:mm:ss
            if (/^\d{2}:\d{2}(:\d{2})?$/.test(normalized)) {
                return normalized.substring(0, 5);
            }
            if (timeBasis === 'UTC') {
                const d = new Date(normalized.includes('T') ? normalized : normalized.replace(' ', 'T'));
                if (!isNaN(d.getTime())) {
                    const hh = String(d.getUTCHours()).padStart(2, '0');
                    const mm = String(d.getUTCMinutes()).padStart(2, '0');
                    return `${hh}:${mm}`;
                }
            } else {
                // Local time
                if (normalized.includes('T')) {
                    const parts = normalized.split('T');
                    if (parts[1] && parts[1].length >= 5) {
                        return parts[1].substring(0, 5);
                    }
                }
                if (normalized.includes(' ')) {
                    const parts = normalized.split(' ');
                    if (parts[1] && parts[1].length >= 5) {
                        return parts[1].substring(0, 5);
                    }
                }
                const d = new Date(normalized);
                if (!isNaN(d.getTime())) {
                    const hh = String(d.getHours()).padStart(2, '0');
                    const mm = String(d.getMinutes()).padStart(2, '0');
                    return `${hh}:${mm}`;
                }
            }
            return dateStr;
        } catch {
            return dateStr;
        }
    };

    const formatTelemetryDateTime = (dateStr) => {
        if (!dateStr) return '--';
        try {
            const str = String(dateStr).trim();
            if (str.includes('T')) {
                const parts = str.split('T');
                const datePart = parts[0];
                const timePart = parts[1].split('.')[0].replace('Z', '');
                return `${datePart} ${timePart}`;
            }
            return str.split('.')[0];
        } catch {
            return dateStr;
        }
    };

    const handleInspect5MinBucket = async (bucket) => {
        setSelectedDetail({ type: '5min', item: bucket });
        setBucketTelemetryList([]);
        setBucketTelemetryError(null);

        const rawStartId = bucket.starting_telemetry_id ?? bucket.start_sensor_telemetry_id ?? bucket.start_telemetry_id ?? bucket.starting_id;
        const rawEndId = bucket.ending_telemetry_id ?? bucket.end_sensor_telemetry_id ?? bucket.end_telemetry_id ?? bucket.ending_id;

        if (rawStartId == null || rawEndId == null) {
            setBucketTelemetryError('No telemetry ID range (starting/ending telemetry ID) is recorded for this 5-minute bucket.');
            return;
        }

        const startId = String(rawStartId).trim();
        const endId = String(rawEndId).trim();
        const startNum = Number(startId);
        const endNum = Number(endId);
        const minId = (!isNaN(startNum) && !isNaN(endNum)) ? Math.min(startNum, endNum) : startId;
        const maxId = (!isNaN(startNum) && !isNaN(endNum)) ? Math.max(startNum, endNum) : endId;
        const dateBasis = (timeBasis === 'UTC' || timeBasis === 'utc') ? 'utc' : 'operational';

        setBucketTelemetryLoading(true);
        try {
            console.log(`[MaintenanceAudit] Fetching telemetry for bucket: start=${startId}, end=${endId}, pole=${poleId}, date_basis=${dateBasis}`);

            // 1. Primary endpoint call with exact bucket IDs
            let res = await fetchApiData('maintenance-telemetry-by-id/', {
                pole_id: poleId,
                start_sensor_telemetry_id: startId,
                end_sensor_telemetry_id: endId,
                date_basis: dateBasis
            });

            // 2. If empty or null, try with minId and maxId
            if (!res || !res.data || (Array.isArray(res.data) && res.data.length === 0)) {
                if (String(minId) !== String(startId) || String(maxId) !== String(endId)) {
                    const fallbackRes = await fetchApiData('maintenance-telemetry-by-id/', {
                        pole_id: poleId,
                        start_sensor_telemetry_id: minId,
                        end_sensor_telemetry_id: maxId,
                        date_basis: dateBasis
                    });
                    if (fallbackRes && fallbackRes.data && (!Array.isArray(fallbackRes.data) || fallbackRes.data.length > 0)) {
                        res = fallbackRes;
                    }
                }
            }

            // 3. Fallback alternative routes
            if (!res || !res.data || (Array.isArray(res.data) && res.data.length === 0)) {
                const alt1 = await fetchApiData('maintenance-telemetry-by-id', {
                    pole_id: poleId,
                    start_sensor_telemetry_id: startId,
                    end_sensor_telemetry_id: endId,
                    date_basis: dateBasis
                });
                if (alt1 && alt1.data) res = alt1;
            }
            if (!res || !res.data || (Array.isArray(res.data) && res.data.length === 0)) {
                const alt2 = await fetchApiData('maintenance_telemetry_by_id/', {
                    pole_id: poleId,
                    start_sensor_telemetry_id: startId,
                    end_sensor_telemetry_id: endId,
                    date_basis: dateBasis
                });
                if (alt2 && alt2.data) res = alt2;
            }

            if (res) {
                const dataArr = extractArrayData(res) || [];
                setBucketTelemetryList(dataArr);
                if (dataArr.length === 0) {
                    setBucketTelemetryError(`No telemetry records returned for IDs ${startId} to ${endId}.`);
                }
            } else {
                setBucketTelemetryError('Unable to fetch telemetry records from server.');
            }
        } catch (err) {
            console.error('[MaintenanceAudit] Error fetching bucket telemetry:', err);
            setBucketTelemetryError('Failed to load telemetry records for this bucket.');
        } finally {
            setBucketTelemetryLoading(false);
        }
    };

    const toggleExpandTelemetry = (id) => {
        setExpandedTelemetryId(prev => prev === id ? null : id);
    };

    const handleExportBucketTelemetryCSV = () => {
        if (!bucketTelemetryList || bucketTelemetryList.length === 0) return;

        const headers = [
            'Sensor Telemetry ID',
            'MQTT Message ID',
            'Received Datetime',
            'Received UTC',
            'Device ID',
            'Topic',
            'Inlet PM1.0 (ug/m3)',
            'Outlet PM1.0 (ug/m3)',
            'Inlet PM2.5 (ug/m3)',
            'Outlet PM2.5 (ug/m3)',
            'Inlet PM4.0 (ug/m3)',
            'Outlet PM4.0 (ug/m3)',
            'Inlet PM10 (ug/m3)',
            'Outlet PM10 (ug/m3)',
            'Inlet Temp (C)',
            'Outlet Temp (C)',
            'Inlet Humidity (%)',
            'Outlet Humidity (%)',
            'Inlet CO2 (ppm)',
            'Outlet CO2 (ppm)',
            'Inlet Particle Count 0.5',
            'Outlet Particle Count 0.5',
            'Inlet Particle Count 1.0',
            'Outlet Particle Count 1.0',
            'Inlet Particle Count 2.5',
            'Outlet Particle Count 2.5',
            'Inlet Particle Count 4.0',
            'Outlet Particle Count 4.0',
            'Inlet Particle Count 10',
            'Outlet Particle Count 10',
            'Pressure (Pa)',
            'Device Health',
            'Device State',
            'Signal (dBm)',
            'Publish Interval (s)',
            'Fan Mode',
            'Fan Speed In (%)',
            'Fan Speed Out (%)',
            'Created UTC'
        ];

        const rows = bucketTelemetryList.map(t => [
            `"${t.sensor_telemetry_id ?? ''}"`,
            `"${t.mqtt_message_id ?? ''}"`,
            `"${t.received_datetime ?? ''}"`,
            `"${t.received_utc ?? ''}"`,
            `"${t.device_id ?? ''}"`,
            `"${t.topic ?? ''}"`,
            `"${t.inlet_pm1_0_ug_m3 ?? ''}"`,
            `"${t.outlet_pm1_0_ug_m3 ?? ''}"`,
            `"${t.inlet_pm2_5_ug_m3 ?? ''}"`,
            `"${t.outlet_pm2_5_ug_m3 ?? ''}"`,
            `"${t.inlet_pm4_0_ug_m3 ?? ''}"`,
            `"${t.outlet_pm4_0_ug_m3 ?? ''}"`,
            `"${t.inlet_pm10_ug_m3 ?? ''}"`,
            `"${t.outlet_pm10_ug_m3 ?? ''}"`,
            `"${t.inlet_temperature_c ?? ''}"`,
            `"${t.outlet_temperature_c ?? ''}"`,
            `"${t.inlet_humidity_percent ?? ''}"`,
            `"${t.outlet_humidity_percent ?? ''}"`,
            `"${t.inlet_co2_ppm ?? ''}"`,
            `"${t.outlet_co2_ppm ?? ''}"`,
            `"${t.inlet_particlecount_0_5 ?? ''}"`,
            `"${t.outlet_particlecount_0_5 ?? ''}"`,
            `"${t.inlet_particlecount_1_0 ?? ''}"`,
            `"${t.outlet_particlecount_1_0 ?? ''}"`,
            `"${t.inlet_particlecount_2_5 ?? ''}"`,
            `"${t.outlet_particlecount_2_5 ?? ''}"`,
            `"${t.inlet_particlecount_4_0 ?? ''}"`,
            `"${t.outlet_particlecount_4_0 ?? ''}"`,
            `"${t.inlet_particlecount_10 ?? ''}"`,
            `"${t.outlet_particlecount_10 ?? ''}"`,
            `"${t.pressure_pa ?? ''}"`,
            `"${t.device_health ?? ''}"`,
            `"${t.device_state ?? ''}"`,
            `"${t.signal_strength_dbm ?? ''}"`,
            `"${t.publish_interval_sec ?? ''}"`,
            `"${t.fan_mode ?? ''}"`,
            `"${t.fan_speed_in_percent ?? ''}"`,
            `"${t.fan_speed_out_percent ?? ''}"`,
            `"${t.created_utc ?? ''}"`
        ]);

        const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `${poleId}_telemetry_${selectedDetail?.item?.starting_telemetry_id}_${selectedDetail?.item?.ending_telemetry_id}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        triggerAlert('success', `Exported ${bucketTelemetryList.length} telemetry records to CSV!`);
    };

    const formatIssueTypeName = (typeStr) => {
        if (!typeStr) return 'Unknown Issue';
        return String(typeStr)
            .replace(/[-_]/g, ' ')
            .split(' ')
            .map(w => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ');
    };

    const getIssueMessage = (issue) => {
        if (!issue) return '--';
        if (issue.message) return issue.message;
        if (issue.description) return issue.description;
        if (issue.detail) return issue.detail;
        const typeStr = (issue.issue_type || issue.type || '').toLowerCase();
        if (typeStr.includes('fan_coverage') || typeStr.includes('fan coverage')) {
            return 'Fan-speed timeline did not cover full bucket.';
        }
        if (typeStr.includes('zero_pm10') || typeStr.includes('zero pm10')) {
            return 'PM10 efficiency could not be calculated.';
        }
        if (typeStr.includes('no_telemetry') || typeStr.includes('no telemetry')) {
            return 'No telemetry samples in bucket.';
        }
        if (typeStr.includes('invalid_fan') || typeStr.includes('invalid fan')) {
            return 'Fan speed outside valid range.';
        }
        if (typeStr.includes('zero_pm25') || typeStr.includes('zero pm2.5')) {
            return 'PM2.5 efficiency could not be calculated.';
        }
        if (typeStr.includes('telemetry_gap') || typeStr.includes('telemetry gap')) {
            return 'Telemetry data gap detected in interval.';
        }
        return issue.issue_type || issue.type || '--';
    };

    const getIssueSeverity = (issue) => {
        if (!issue) return 'Error';
        if (issue.severity) {
            const s = String(issue.severity);
            return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
        }
        const typeStr = (issue.issue_type || issue.type || '').toLowerCase();
        if (typeStr.includes('invalid_fan') || typeStr.includes('warning') || typeStr.includes('watch')) {
            return 'Warning';
        }
        return 'Error';
    };

    const getIssueTimes = (issue) => {
        if (!issue) return { start: '--', end: '--' };

        const rawStart = issue.bucket_start_date || issue.bucket_start_time || issue.bucket_start || issue.start_time || issue.start_date || issue.start;
        const rawEnd = issue.bucket_end_date || issue.bucket_end_time || issue.bucket_end || issue.end_time || issue.end_date || issue.end;

        if (rawStart || rawEnd) {
            return {
                start: rawStart ? formatTimeDisplay(rawStart) : '--',
                end: rawEnd ? formatTimeDisplay(rawEnd) : '--'
            };
        }

        if (issue.last_occurrence_datetime || issue.datetime || issue.timestamp) {
            const dt = issue.last_occurrence_datetime || issue.datetime || issue.timestamp;
            const endFormatted = formatTimeDisplay(dt);
            try {
                const d = new Date(dt.includes('T') ? dt : dt.replace(' ', 'T'));
                if (!isNaN(d.getTime())) {
                    const startD = new Date(d.getTime() - 5 * 60 * 1000);
                    return {
                        start: formatTimeDisplay(startD.toISOString()),
                        end: endFormatted
                    };
                }
            } catch {
                // fallback
            }
            return { start: '--', end: endFormatted };
        }

        return { start: '--', end: '--' };
    };

    // Overview Key-Values Pairs (Dynamic with no hardcoded fallback constants)
    const auditOverviewPairs = [
        {
            col1: {
                label: 'Total Air Cleaned',
                value: overviewData?.total_air_cleaned != null
                    ? `${Math.round(overviewData.total_air_cleaned).toLocaleString()} cu ft`
                    : '--'
            },
            col2: {
                label: 'Average Temperature',
                value: overviewData?.average_temperature != null
                    ? `${Number(overviewData.average_temperature).toFixed(1)}°C`
                    : (pole?.temperature_c != null ? `${Number(pole.temperature_c).toFixed(1)}°C` : '--')
            }
        },
        {
            col1: {
                label: 'Total Air Processed',
                value: overviewData?.total_air_processed != null
                    ? `${Math.round(overviewData.total_air_processed).toLocaleString()} cu ft`
                    : '--'
            },
            col2: {
                label: 'Average Humidity',
                value: overviewData?.average_humidity != null
                    ? `${Math.round(overviewData.average_humidity)}%`
                    : (pole?.humidity_percent != null ? `${Math.round(pole.humidity_percent)}%` : '--')
            }
        },
        {
            col1: {
                label: 'Average Efficiency',
                value: overviewData?.average_efficiency != null
                    ? `${Number(overviewData.average_efficiency).toFixed(1)}%`
                    : '--'
            },
            col2: {
                label: 'Average Pressure',
                value: overviewData?.average_pressure != null
                    ? `${Math.round(overviewData.average_pressure)} hPa`
                    : '--'
            }
        },
        {
            col1: {
                label: 'Average Fan %',
                value: overviewData?.average_fan_speed != null
                    ? `${Math.round(overviewData.average_fan_speed)}%`
                    : '--'
            },
            col2: {
                label: '5-Min Buckets',
                value: overviewData?.five_minute_bucket_count != null
                    ? `${overviewData.five_minute_bucket_count}`
                    : (fiveMinBuckets.length > 0 ? `${fiveMinBuckets.length}` : '--')
            }
        },
        {
            col1: {
                label: 'Uptime',
                value: overviewData?.uptime != null
                    ? `${Number(overviewData.uptime).toFixed(1)}%`
                    : (fiveMinBuckets.length > 0 ? `${((fiveMinBuckets.filter(b => (b.status || '').toLowerCase() !== 'error').length / fiveMinBuckets.length) * 100).toFixed(1)}%` : '--')
            },
            col2: {
                label: 'Issues',
                value: overviewData?.issue_count != null
                    ? `${overviewData.issue_count}`
                    : (topIssueTypes.length > 0 || recentIssues.length > 0 ? `${topIssueTypes.reduce((acc, t) => acc + (Number(t.issue_count || t.count) || 1), 0) || recentIssues.length}` : '--')
            }
        }
    ];

    // Status Distribution Table Rows
    const getStatusDistributionRows = (distData) => {
        if (!distData || distData.length === 0) {
            return {
                items: [],
                totalCount: 0
            };
        }

        const totalCount = distData.reduce((acc, curr) => acc + (Number(curr.bucket_count) || 0), 0);

        return {
            items: distData,
            totalCount: totalCount
        };
    };

    // ─── 5-MIN PAGINATION DATA ────────────────────────────────
    const displayed5MinBuckets = fiveMinBuckets.slice(
        (fiveMinPage - 1) * pageSize5Min,
        fiveMinPage * pageSize5Min
    );
    const total5MinPages = Math.ceil(fiveMinBuckets.length / pageSize5Min) || 1;

    // ─── RECENT ISSUES PAGINATION DATA ────────────────────────
    const displayedRecentIssues = recentIssues.slice(
        (recentIssuesPage - 1) * pageSizeRecentIssues,
        recentIssuesPage * pageSizeRecentIssues
    );
    const totalRecentIssuesPages = Math.ceil(recentIssues.length / pageSizeRecentIssues) || 1;

    // ─── HOURLY PAGINATION DATA ───────────────────────────────
    const displayedHourlyBuckets = hourlyBuckets.slice(
        (hourlyPage - 1) * pageSizeHourly,
        hourlyPage * pageSizeHourly
    );
    const totalHourlyPages = Math.ceil(hourlyBuckets.length / pageSizeHourly) || 1;

    // ─── DAILY PAGINATION DATA ────────────────────────────────
    const displayedDailyBuckets = dailyBuckets.slice(
        (dailyPage - 1) * pageSizeDaily,
        dailyPage * pageSizeDaily
    );
    const totalDailyPages = Math.ceil(dailyBuckets.length / pageSizeDaily) || 1;

    // ─── 24H TELEMETRY COMPUTED STATS & FILTERING ─────────────
    const latestTelemetry = (telemetryRecords && telemetryRecords.length > 0) ? telemetryRecords[0] : null;

    // Filtered Telemetry Stream
    const filteredTelemetry = telemetryRecords.filter(t => {
        if (!telemetrySearch) return true;
        const q = telemetrySearch.toLowerCase().trim();
        const idMatch = String(t.sensor_telemetry_id || '').toLowerCase().includes(q);
        const mqttMatch = String(t.mqtt_message_id || '').toLowerCase().includes(q);
        const healthMatch = String(t.device_health || '').toLowerCase().includes(q);
        const stateMatch = String(t.device_state || '').toLowerCase().includes(q);
        const timeMatch = String(t.received_datetime || t.received_utc || '').toLowerCase().includes(q);
        const modeMatch = String(t.fan_mode || '').toLowerCase().includes(q);
        return idMatch || mqttMatch || healthMatch || stateMatch || timeMatch || modeMatch;
    });

    const totalTelemetryPages = Math.ceil(filteredTelemetry.length / telemetryPageSize) || 1;
    const displayedTelemetry = filteredTelemetry.slice(
        (telemetryPage - 1) * telemetryPageSize,
        telemetryPage * telemetryPageSize
    );

    // Calculate averages over the fetched 24h stream
    const avgTelemetryMetrics = (() => {
        if (!telemetryRecords || telemetryRecords.length === 0) {
            return {
                pm25In: null, pm25Out: null, pm25Eff: null,
                pm10In: null, pm10Out: null, pm10Eff: null,
                tempIn: null, tempOut: null,
                humIn: null, humOut: null,
                co2In: null, co2Out: null
            };
        }

        const validPm25In = telemetryRecords.filter(t => t.inlet_pm2_5_ug_m3 != null);
        const avgPm25In = validPm25In.length > 0 ? validPm25In.reduce((s, t) => s + Number(t.inlet_pm2_5_ug_m3), 0) / validPm25In.length : null;
        const validPm25Out = telemetryRecords.filter(t => t.outlet_pm2_5_ug_m3 != null);
        const avgPm25Out = validPm25Out.length > 0 ? validPm25Out.reduce((s, t) => s + Number(t.outlet_pm2_5_ug_m3), 0) / validPm25Out.length : null;
        const pm25Eff = (avgPm25In != null && avgPm25Out != null && avgPm25In > 0) ? ((avgPm25In - avgPm25Out) / avgPm25In) * 100 : null;

        const validPm10In = telemetryRecords.filter(t => t.inlet_pm10_ug_m3 != null);
        const avgPm10In = validPm10In.length > 0 ? validPm10In.reduce((s, t) => s + Number(t.inlet_pm10_ug_m3), 0) / validPm10In.length : null;
        const validPm10Out = telemetryRecords.filter(t => t.outlet_pm10_ug_m3 != null);
        const avgPm10Out = validPm10Out.length > 0 ? validPm10Out.reduce((s, t) => s + Number(t.outlet_pm10_ug_m3), 0) / validPm10Out.length : null;
        const pm10Eff = (avgPm10In != null && avgPm10Out != null && avgPm10In > 0) ? ((avgPm10In - avgPm10Out) / avgPm10In) * 100 : null;

        const validTempIn = telemetryRecords.filter(t => t.inlet_temperature_c != null);
        const avgTempIn = validTempIn.length > 0 ? validTempIn.reduce((s, t) => s + Number(t.inlet_temperature_c), 0) / validTempIn.length : null;
        const validTempOut = telemetryRecords.filter(t => t.outlet_temperature_c != null);
        const avgTempOut = validTempOut.length > 0 ? validTempOut.reduce((s, t) => s + Number(t.outlet_temperature_c), 0) / validTempOut.length : null;

        const validHumIn = telemetryRecords.filter(t => t.inlet_humidity_percent != null);
        const avgHumIn = validHumIn.length > 0 ? validHumIn.reduce((s, t) => s + Number(t.inlet_humidity_percent), 0) / validHumIn.length : null;
        const validHumOut = telemetryRecords.filter(t => t.outlet_humidity_percent != null);
        const avgHumOut = validHumOut.length > 0 ? validHumOut.reduce((s, t) => s + Number(t.outlet_humidity_percent), 0) / validHumOut.length : null;

        const validCo2In = telemetryRecords.filter(t => t.inlet_co2_ppm != null);
        const avgCo2In = validCo2In.length > 0 ? validCo2In.reduce((s, t) => s + Number(t.inlet_co2_ppm), 0) / validCo2In.length : null;
        const validCo2Out = telemetryRecords.filter(t => t.outlet_co2_ppm != null);
        const avgCo2Out = validCo2Out.length > 0 ? validCo2Out.reduce((s, t) => s + Number(t.outlet_co2_ppm), 0) / validCo2Out.length : null;

        return {
            pm25In: avgPm25In, pm25Out: avgPm25Out, pm25Eff,
            pm10In: avgPm10In, pm10Out: avgPm10Out, pm10Eff,
            tempIn: avgTempIn, tempOut: avgTempOut,
            humIn: avgHumIn, humOut: avgHumOut,
            co2In: avgCo2In, co2Out: avgCo2Out
        };
    })();

    // ─── MAINTENANCE COMMANDS COMPUTED STATS & FILTERING ─────
    // Extract unique actions for dynamic filter pills
    const uniqueCommandActions = Array.from(
        new Set(commandRecords.map(c => c.action || c.command_name).filter(Boolean))
    );

    // Filtered Commands List
    const filteredCommands = commandRecords.filter(c => {
        // Action filter
        if (commandsActionFilter !== 'all') {
            const act = (c.action || c.command_name || '').toLowerCase();
            if (act !== commandsActionFilter.toLowerCase()) return false;
        }

        // Search query filter
        if (!commandsSearch) return true;
        const q = commandsSearch.toLowerCase().trim();
        const nameMatch = String(c.command_name || '').toLowerCase().includes(q);
        const actionMatch = String(c.action || '').toLowerCase().includes(q);
        const createdMatch = String(c.created_datetime || '').toLowerCase().includes(q);
        const sentMatch = String(c.sent_datetime || '').toLowerCase().includes(q);
        const payloadMatch = String(typeof c.payload_json === 'string' ? c.payload_json : JSON.stringify(c.payload_json || '')).toLowerCase().includes(q);
        return nameMatch || actionMatch || createdMatch || sentMatch || payloadMatch;
    });

    const totalCommandsPages = Math.ceil(filteredCommands.length / commandsPageSize) || 1;
    const displayedCommands = filteredCommands.slice(
        (commandsPage - 1) * commandsPageSize,
        commandsPage * commandsPageSize
    );

    // Compute Commands Summary Stats
    const commandsSummaryStats = (() => {
        if (!commandRecords || commandRecords.length === 0) {
            return {
                totalCount: 0,
                uniqueActionsCount: 0,
                latestCommand: null,
                avgLatencyMs: null,
                latestFanIn: null,
                latestFanOut: null
            };
        }

        const latencies = commandRecords
            .map(c => calculateLatency(c.created_datetime, c.sent_datetime))
            .filter(l => l != null);

        const avgLat = latencies.length > 0
            ? Math.round(latencies.reduce((s, l) => s + l, 0) / latencies.length)
            : null;

        const latest = commandRecords[0];
        const parsedLatest = parseCommandPayload(latest?.payload_json);
        const latestObj = parsedLatest?.command || parsedLatest || {};

        return {
            totalCount: commandRecords.length,
            uniqueActionsCount: uniqueCommandActions.length,
            latestCommand: latest,
            avgLatencyMs: avgLat,
            latestFanIn: latestObj.fan_speed_in_percent,
            latestFanOut: latestObj.fan_speed_out_percent
        };
    })();

    return (
        <div className="m-audit-container" onClick={(e) => e.stopPropagation()}>
            {/* 1. SUB-TABS NAVIGATION */}
            <div className="m-audit-subtabs-bar">
                {AUDIT_SUB_TABS.map((tab) => (
                    <button
                        key={tab}
                        className={`m-audit-subtab-btn ${activeSubTab === tab ? 'active' : ''}`}
                        onClick={() => setActiveSubTab(tab)}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            {/* 2. TOP CONTROLS & FILTER TOOLBAR */}
            <div className="m-audit-toolbar">
                <div className="m-audit-toolbar-left">
                    <div className="m-audit-date-range-group">
                        <span className="m-audit-label">Date Range:</span>

                        {/* Calendar Icon Box */}
                        <div
                            className="m-audit-cal-icon-box"
                            title="Open Calendar Picker"
                            onClick={handleOpenStartPicker}
                        >
                            <Calendar size={15} />
                        </div>

                        {/* Start Date & Time Picker */}
                        <div className="m-audit-input-wrap" onClick={handleOpenStartPicker}>
                            <span className="m-audit-input-prefix">Start</span>
                            <input
                                ref={startInputRef}
                                type="datetime-local"
                                className="m-audit-input m-audit-datetime-input"
                                value={toInputDateTime(startDate)}
                                onChange={(e) => {
                                    const newDate = fromInputDateTime(e.target.value);
                                    setStartDate(newDate);
                                    setActivePreset(null);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSearch();
                                }}
                            />
                            <Calendar
                                className="m-audit-input-icon"
                                size={14}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenStartPicker();
                                }}
                            />
                        </div>

                        {/* End Date & Time Picker */}
                        <div className="m-audit-input-wrap" onClick={handleOpenEndPicker}>
                            <span className="m-audit-input-prefix">End</span>
                            <input
                                ref={endInputRef}
                                type="datetime-local"
                                className="m-audit-input m-audit-datetime-input"
                                value={toInputDateTime(endDate)}
                                onChange={(e) => {
                                    const newDate = fromInputDateTime(e.target.value);
                                    setEndDate(newDate);
                                    setActivePreset(null);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSearch();
                                }}
                            />
                            <Calendar
                                className="m-audit-input-icon"
                                size={14}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenEndPicker();
                                }}
                            />
                        </div>

                        {/* Search Button */}
                        <button
                            type="button"
                            className="m-audit-search-btn"
                            onClick={handleSearch}
                            title="Search audit records for selected date range"
                        >
                            <Search size={13} />
                            <span>Search</span>
                        </button>

                        {/* Quick Presets */}
                        <div className="m-audit-presets">
                            <button
                                type="button"
                                className={`m-audit-preset-btn ${activePreset === 'today' ? 'active' : ''}`}
                                onClick={setTodayPreset}
                                title="Set Today (00:00 to now)"
                            >
                                Today
                            </button>
                            <button
                                type="button"
                                className={`m-audit-preset-btn ${activePreset === 'last24h' ? 'active' : ''}`}
                                onClick={setLast24HoursPreset}
                                title="Set Last 24 Hours to Current Time"
                            >
                                Last 24H
                            </button>
                            <button
                                type="button"
                                className={`m-audit-preset-btn ${activePreset === 'last7d' ? 'active' : ''}`}
                                onClick={setLast7DaysPreset}
                                title="Set Last 7 Days"
                            >
                                Last 7D
                            </button>
                            <button
                                type="button"
                                className={`m-audit-preset-btn ${activePreset === 'last30d' ? 'active' : ''}`}
                                onClick={setLast30DaysPreset}
                                title="Set Last 30 Days"
                            >
                                Last 30D
                            </button>
                        </div>
                    </div>

                    <div className="m-audit-time-basis-group">
                        <span className="m-audit-label">Time Basis:</span>
                        <div className="m-audit-toggle-group">
                            <button
                                type="button"
                                className={`m-audit-toggle-btn ${timeBasis === 'UTC' ? 'active' : ''}`}
                                onClick={() => setTimeBasis('UTC')}
                            >
                                UTC
                            </button>
                            <button
                                type="button"
                                className={`m-audit-toggle-btn ${timeBasis === 'Local' ? 'active' : ''}`}
                                onClick={() => setTimeBasis('Local')}
                            >
                                Local
                            </button>
                        </div>
                    </div>
                </div>

                <div className="m-audit-toolbar-right">
                    <button
                        type="button"
                        className={`m-audit-action-btn m-audit-refresh-btn ${isRefreshing ? 'spinning' : ''}`}
                        onClick={handleRefresh}
                        title="Fetch latest audit data"
                    >
                        <RefreshCw size={14} className={isRefreshing ? 'spin-icon' : ''} />
                        <span>Refresh</span>
                    </button>
                    <button
                        type="button"
                        className="m-audit-action-btn m-audit-export-btn"
                        onClick={handleExportCSV}
                        title="Export current tab data as CSV"
                    >
                        <Download size={14} />
                        <span>Export CSV</span>
                    </button>
                </div>
            </div>

            {/* IN-PANEL ALERT / TOAST FEEDBACK */}
            {panelAlert && (
                <div className={`m-audit-alert ${panelAlert.type}`}>
                    <div className="m-audit-alert-icon">
                        {panelAlert.type === 'success' && <CheckCircle size={15} />}
                        {panelAlert.type === 'error' && <AlertTriangle size={15} />}
                        {panelAlert.type === 'info' && <Info size={15} />}
                    </div>
                    <span className="m-audit-alert-text">{panelAlert.message}</span>
                    <button
                        type="button"
                        className="m-audit-alert-close"
                        onClick={() => setPanelAlert(null)}
                        title="Dismiss alert"
                    >
                        <X size={13} />
                    </button>
                </div>
            )}

            {/* 3. SCREEN 1: 5-MINUTE AUDIT VIEW */}
            {activeSubTab === '5-Minute Audit' && (
                <div className="m-audit-content">
                    {/* Top Row: Overview & Recent 5-Min Buckets */}
                    <div className="m-audit-grid-row m-audit-row-top">
                        {/* Audit Overview Card */}
                        <div className="m-audit-card m-audit-card-overview">
                            <div className="m-audit-card-header">
                                <h4>Audit Overview (Selected Date Range)</h4>
                            </div>
                            <div className="m-audit-overview-grid">
                                {auditOverviewPairs.map((pair, idx) => (
                                    <div key={idx} className="m-overview-pair-row">
                                        <div className="m-overview-item">
                                            <span className="m-overview-key">{pair.col1.label}</span>
                                            <span className="m-overview-val">{pair.col1.value}</span>
                                        </div>
                                        <div className="m-overview-item">
                                            <span className="m-overview-key">{pair.col2.label}</span>
                                            <span className="m-overview-val">{pair.col2.value}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Recent 5-Minute Buckets Table */}
                        <div className="m-audit-card m-audit-card-buckets">
                            <div className="m-audit-card-header m-header-with-badge">
                                <h4>Recent 5-Minute Buckets</h4>
                                <span className="m-header-count-tag">{fiveMinBuckets.length} buckets</span>
                            </div>
                            <div className="m-audit-table-wrapper">
                                <table className="m-audit-table">
                                    <thead>
                                        <tr>
                                            <th>Start Time ({timeBasis})</th>
                                            <th>End Time ({timeBasis})</th>
                                            <th>PM10 Inlet</th>
                                            <th>PM10 Outlet</th>
                                            <th>PM2.5 Inlet</th>
                                            <th>PM2.5 Outlet</th>
                                            <th>Eff. %</th>
                                            <th>Fan %</th>
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayed5MinBuckets.length === 0 ? (
                                            <tr>
                                                <td colSpan="10" className="m-table-empty-cell">
                                                    {loading ? 'Loading 5-minute audit data...' : 'No 5-minute audit records found for selected date range'}
                                                </td>
                                            </tr>
                                        ) : (
                                            displayed5MinBuckets.map((b, idx) => (
                                                <tr key={idx}>
                                                    <td>{formatTimeDisplay(b.start_time)}</td>
                                                    <td>{formatTimeDisplay(b.end_time)}</td>
                                                    <td>{b.pm10_inlet != null ? Number(b.pm10_inlet).toFixed(1) : '--'}</td>
                                                    <td>{b.pm10_outlet != null ? Number(b.pm10_outlet).toFixed(1) : '--'}</td>
                                                    <td>{b.pm25_inlet != null ? Number(b.pm25_inlet).toFixed(1) : '--'}</td>
                                                    <td>{b.pm25_outlet != null ? Number(b.pm25_outlet).toFixed(1) : '--'}</td>
                                                    <td>{b.efficiency_percentage != null ? `${Number(b.efficiency_percentage).toFixed(1)}%` : '--'}</td>
                                                    <td>{b.fan_percentage != null ? `${Math.round(b.fan_percentage)}%` : '--'}</td>
                                                    <td>
                                                        <span className={`m-badge m-badge-${(b.status || '--').toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                                                            {b.status || '--'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <button
                                                            type="button"
                                                            className="m-table-action-icon"
                                                            title="Inspect Telemetry Records"
                                                            onClick={() => handleInspect5MinBucket(b)}
                                                        >
                                                            <Eye size={14} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {total5MinPages > 1 && (
                                <div className="m-audit-pagination-bar">
                                    <span className="m-pagination-info">
                                        Showing {(fiveMinPage - 1) * pageSize5Min + 1} - {Math.min(fiveMinPage * pageSize5Min, fiveMinBuckets.length)} of {fiveMinBuckets.length}
                                    </span>
                                    <div className="m-pagination-btns">
                                        <button
                                            type="button"
                                            className="m-audit-page-btn"
                                            disabled={fiveMinPage === 1}
                                            onClick={() => setFiveMinPage(p => Math.max(1, p - 1))}
                                        >
                                            <ChevronLeft size={14} />
                                        </button>
                                        <span className="m-page-indicator">{fiveMinPage} / {total5MinPages}</span>
                                        <button
                                            type="button"
                                            className="m-audit-page-btn"
                                            disabled={fiveMinPage === total5MinPages}
                                            onClick={() => setFiveMinPage(p => Math.min(total5MinPages, p + 1))}
                                        >
                                            <ChevronRight size={14} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Bottom Row: Status Distribution, Top Issue Types, Recent Issues */}
                    <div className="m-audit-grid-row m-audit-row-bottom">
                        {/* Status Distribution (5-Min Buckets) */}
                        <div className="m-audit-card m-audit-card-distribution">
                            <div className="m-audit-card-header">
                                <h4>Status Distribution (5-Min Buckets)</h4>
                            </div>
                            <div className="m-audit-table-wrapper">
                                <table className="m-audit-table m-audit-table-dist">
                                    <thead>
                                        <tr>
                                            <th>Status</th>
                                            <th>Count</th>
                                            <th>Percentage</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(() => {
                                            const { items, totalCount } = getStatusDistributionRows(fiveMinStatusDist);
                                            if (items.length === 0) {
                                                return (
                                                    <tr>
                                                        <td colSpan="3" className="m-table-empty-cell">
                                                            No status distribution data available
                                                        </td>
                                                    </tr>
                                                );
                                            }
                                            return (
                                                <>
                                                    {items.map((item, index) => {
                                                        const sName = item.status || '--';
                                                        const sType = sName.toLowerCase().replace(/[^a-z0-9]/g, '-');
                                                        const percentVal = item.bucket_percent != null
                                                            ? `${Number(item.bucket_percent).toFixed(1)}%`
                                                            : (totalCount > 0 ? `${Math.round((item.bucket_count / totalCount) * 100)}%` : '--');

                                                        return (
                                                            <tr key={index}>
                                                                <td>
                                                                    <div className="m-status-cell">
                                                                        <span className={`m-status-dot dot-${sType}`} />
                                                                        <span>{sName}</span>
                                                                    </div>
                                                                </td>
                                                                <td>{item.bucket_count ?? 0}</td>
                                                                <td>{percentVal}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                    <tr className="m-row-total">
                                                        <td><strong>Total</strong></td>
                                                        <td><strong>{totalCount}</strong></td>
                                                        <td><strong>{totalCount > 0 ? '100%' : '--'}</strong></td>
                                                    </tr>
                                                </>
                                            );
                                        })()}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Top Issue Types */}
                        <div className="m-audit-card m-audit-card-top-issues">
                            <div className="m-audit-card-header">
                                <h4>Top Issue Types</h4>
                            </div>
                            <div className="m-audit-table-wrapper">
                                <table className="m-audit-table">
                                    <thead>
                                        <tr>
                                            <th>Issue Type</th>
                                            <th>Count</th>
                                            <th>Last Occurrence</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {topIssueTypes.length === 0 ? (
                                            <tr>
                                                <td colSpan="3" className="m-table-empty-cell">
                                                    {loading ? 'Loading issue types...' : 'No issue types for selected date range'}
                                                </td>
                                            </tr>
                                        ) : (
                                            topIssueTypes.map((issue, idx) => (
                                                <tr key={idx}>
                                                    <td className="m-cell-issuetype">{formatIssueTypeName(issue.issue_type || issue.type)}</td>
                                                    <td>{issue.issue_count ?? issue.count ?? 1}</td>
                                                    <td className="m-cell-muted">
                                                        {issue.last_occurrence_datetime
                                                            ? issue.last_occurrence_datetime.replace('T', ' ')
                                                            : issue.lastOccurrence || '--'}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div className="m-card-footer">
                                <button type="button" className="m-view-all-btn" onClick={() => setActiveSubTab('Issues')}>
                                    View All Issues
                                </button>
                            </div>
                        </div>

                        {/* Recent Issues */}
                        <div className="m-audit-card m-audit-card-recent-issues">
                            <div className="m-audit-card-header m-header-with-badge">
                                <h4>Recent Issues</h4>
                                <span className="m-header-count-tag">{recentIssues.length} issues</span>
                            </div>
                            <div className="m-audit-table-wrapper">
                                <table className="m-audit-table">
                                    <thead>
                                        <tr>
                                            <th>Bucket Start ({timeBasis})</th>
                                            <th>Bucket End ({timeBasis})</th>
                                            <th>Issue Type</th>
                                            <th>Severity</th>
                                            <th>Message</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayedRecentIssues.length === 0 ? (
                                            <tr>
                                                <td colSpan="6" className="m-table-empty-cell">
                                                    {loading ? 'Loading recent issues...' : 'No recent issues recorded for selected date range'}
                                                </td>
                                            </tr>
                                        ) : (
                                            displayedRecentIssues.map((issue, idx) => {
                                                const times = getIssueTimes(issue);
                                                const sev = getIssueSeverity(issue);
                                                return (
                                                    <tr key={idx}>
                                                        <td>{times.start}</td>
                                                        <td>{times.end}</td>
                                                        <td className="m-cell-issuetype">
                                                            {formatIssueTypeName(issue.issue_type || issue.type)}
                                                        </td>
                                                        <td>
                                                            <span className={`m-severity-badge severity-${sev.toLowerCase()}`}>
                                                                {sev}
                                                            </span>
                                                        </td>
                                                        <td className="m-cell-message" title={getIssueMessage(issue)}>
                                                            {getIssueMessage(issue)}
                                                        </td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="m-table-action-icon"
                                                                title="Inspect Issue"
                                                                onClick={() => setSelectedDetail({ type: 'issue', item: issue })}
                                                            >
                                                                <Eye size={14} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {totalRecentIssuesPages > 1 && (
                                <div className="m-audit-pagination-bar">
                                    <span className="m-pagination-info">
                                        Showing {(recentIssuesPage - 1) * pageSizeRecentIssues + 1} - {Math.min(recentIssuesPage * pageSizeRecentIssues, recentIssues.length)} of {recentIssues.length}
                                    </span>
                                    <div className="m-pagination-btns">
                                        <button
                                            type="button"
                                            className="m-audit-page-btn"
                                            disabled={recentIssuesPage === 1}
                                            onClick={() => setRecentIssuesPage(p => Math.max(1, p - 1))}
                                        >
                                            <ChevronLeft size={14} />
                                        </button>
                                        <span className="m-page-indicator">{recentIssuesPage} / {totalRecentIssuesPages}</span>
                                        <button
                                            type="button"
                                            className="m-audit-page-btn"
                                            disabled={recentIssuesPage === totalRecentIssuesPages}
                                            onClick={() => setRecentIssuesPage(p => Math.min(totalRecentIssuesPages, p + 1))}
                                        >
                                            <ChevronRight size={14} />
                                        </button>
                                    </div>
                                </div>
                            )}
                            <div className="m-card-footer">
                                <button type="button" className="m-view-all-btn" onClick={() => setActiveSubTab('Issues')}>
                                    View All Issues
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 4. SCREEN 2: HOURLY AUDIT TAB */}
            {activeSubTab === 'Hourly Audit' && (
                <div className="m-audit-content">
                    {/* Hourly Summary Cards */}
                    <div className="m-audit-metrics-row">
                        <div className="m-metric-chip">
                            <span className="m-chip-label">Hourly Records</span>
                            <span className="m-chip-val">{hourlyBuckets.length}</span>
                        </div>
                        <div className="m-metric-chip">
                            <span className="m-chip-label">Avg Efficiency</span>
                            <span className="m-chip-val">
                                {hourlyBuckets.length > 0
                                    ? `${(hourlyBuckets.reduce((acc, h) => acc + (Number(h.average_efficiency_percentage) || 0), 0) / hourlyBuckets.length).toFixed(1)}%`
                                    : '--'}
                            </span>
                        </div>
                        <div className="m-metric-chip">
                            <span className="m-chip-label">Total Air Processed</span>
                            <span className="m-chip-val">
                                {hourlyBuckets.length > 0
                                    ? `${Math.round(hourlyBuckets.reduce((acc, h) => acc + (Number(h.total_air_processed) || 0), 0)).toLocaleString()} cu ft`
                                    : '--'}
                            </span>
                        </div>
                        <div className="m-metric-chip">
                            <span className="m-chip-label">Total Air Cleaned</span>
                            <span className="m-chip-val">
                                {hourlyBuckets.length > 0
                                    ? `${Math.round(hourlyBuckets.reduce((acc, h) => acc + (Number(h.total_air_cleaned) || 0), 0)).toLocaleString()} cu ft`
                                    : '--'}
                            </span>
                        </div>
                    </div>

                    {/* Hourly Table & Status Distribution Grid */}
                    <div className="m-audit-grid-row m-audit-row-hourly">
                        <div className="m-audit-card m-audit-card-hourly-table">
                            <div className="m-audit-card-header m-header-with-badge">
                                <h4>Hourly Audit Records</h4>
                                <span className="m-header-count-tag">{hourlyBuckets.length} hours</span>
                            </div>
                            <div className="m-audit-table-wrapper">
                                <table className="m-audit-table">
                                    <thead>
                                        <tr>
                                            <th>Start Time ({timeBasis})</th>
                                            <th>End Time ({timeBasis})</th>
                                            <th>5-Min Buckets</th>
                                            <th>Complete</th>
                                            <th>Incomplete</th>
                                            <th>Samples</th>
                                            <th>Avg Eff. %</th>
                                            <th>Effective Eff. %</th>
                                            <th>Fan %</th>
                                            <th>Air Processed</th>
                                            <th>Air Cleaned</th>
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayedHourlyBuckets.length === 0 ? (
                                            <tr>
                                                <td colSpan="13" className="m-table-empty-cell">
                                                    {loading ? 'Loading hourly audit data...' : 'No hourly audit records found for selected date range'}
                                                </td>
                                            </tr>
                                        ) : (
                                            displayedHourlyBuckets.map((h, idx) => (
                                                <tr key={idx}>
                                                    <td>{formatTimeDisplay(h.start_time)}</td>
                                                    <td>{formatTimeDisplay(h.end_time)}</td>
                                                    <td>{h.five_minute_bucket_count ?? '--'}</td>
                                                    <td><span className="m-text-success">{h.complete_bucket_count ?? '--'}</span></td>
                                                    <td>
                                                        <span className={h.incomplete_bucket_count > 0 ? 'm-text-danger' : 'm-text-muted'}>
                                                            {h.incomplete_bucket_count ?? 0}
                                                        </span>
                                                    </td>
                                                    <td>{h.sample_count ?? '--'}</td>
                                                    <td>{h.average_efficiency_percentage != null ? `${Number(h.average_efficiency_percentage).toFixed(1)}%` : '--'}</td>
                                                    <td>{h.effective_efficiency_percentage != null ? `${Number(h.effective_efficiency_percentage).toFixed(1)}%` : '--'}</td>
                                                    <td>{h.fan_percentage != null ? `${Math.round(h.fan_percentage)}%` : '--'}</td>
                                                    <td>{h.total_air_processed != null ? `${Math.round(h.total_air_processed).toLocaleString()} cu ft` : '--'}</td>
                                                    <td>{h.total_air_cleaned != null ? `${Math.round(h.total_air_cleaned).toLocaleString()} cu ft` : '--'}</td>
                                                    <td>
                                                        <span className={`m-badge m-badge-${(h.status || '--').toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                                                            {h.status || '--'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <button
                                                            type="button"
                                                            className="m-table-action-icon"
                                                            title="Inspect Hour"
                                                            onClick={() => setSelectedDetail({ type: 'hourly', item: h })}
                                                        >
                                                            <Eye size={14} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {totalHourlyPages > 1 && (
                                <div className="m-audit-pagination-bar">
                                    <span className="m-pagination-info">
                                        Showing {(hourlyPage - 1) * pageSizeHourly + 1} - {Math.min(hourlyPage * pageSizeHourly, hourlyBuckets.length)} of {hourlyBuckets.length}
                                    </span>
                                    <div className="m-pagination-btns">
                                        <button
                                            type="button"
                                            className="m-audit-page-btn"
                                            disabled={hourlyPage === 1}
                                            onClick={() => setHourlyPage(p => Math.max(1, p - 1))}
                                        >
                                            <ChevronLeft size={14} />
                                        </button>
                                        <span className="m-page-indicator">{hourlyPage} / {totalHourlyPages}</span>
                                        <button
                                            type="button"
                                            className="m-audit-page-btn"
                                            disabled={hourlyPage === totalHourlyPages}
                                            onClick={() => setHourlyPage(p => Math.min(totalHourlyPages, p + 1))}
                                        >
                                            <ChevronRight size={14} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Hourly Status Distribution */}
                        <div className="m-audit-card m-audit-card-hourly-dist">
                            <div className="m-audit-card-header">
                                <h4>Hourly Status Distribution</h4>
                            </div>
                            <div className="m-audit-table-wrapper">
                                <table className="m-audit-table m-audit-table-dist">
                                    <thead>
                                        <tr>
                                            <th>Status</th>
                                            <th>Count</th>
                                            <th>Percentage</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(() => {
                                            const { items, totalCount } = getStatusDistributionRows(hourlyStatusDist);
                                            if (items.length === 0) {
                                                return (
                                                    <tr>
                                                        <td colSpan="3" className="m-table-empty-cell">
                                                            No status distribution data available
                                                        </td>
                                                    </tr>
                                                );
                                            }
                                            return (
                                                <>
                                                    {items.map((item, index) => {
                                                        const sName = item.status || '--';
                                                        const sType = sName.toLowerCase().replace(/[^a-z0-9]/g, '-');
                                                        return (
                                                            <tr key={index}>
                                                                <td>
                                                                    <div className="m-status-cell">
                                                                        <span className={`m-status-dot dot-${sType}`} />
                                                                        <span>{sName}</span>
                                                                    </div>
                                                                </td>
                                                                <td>{item.bucket_count ?? 0}</td>
                                                                <td>{item.bucket_percent != null ? `${Number(item.bucket_percent).toFixed(1)}%` : (totalCount > 0 ? `${Math.round((item.bucket_count / totalCount) * 100)}%` : '--')}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                    <tr className="m-row-total">
                                                        <td><strong>Total</strong></td>
                                                        <td><strong>{totalCount}</strong></td>
                                                        <td><strong>{totalCount > 0 ? '100%' : '--'}</strong></td>
                                                    </tr>
                                                </>
                                            );
                                        })()}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 5. SCREEN 3: DAILY AUDIT TAB */}
            {activeSubTab === 'Daily Audit' && (
                <div className="m-audit-content">
                    <div className="m-audit-grid-row m-audit-row-daily">
                        <div className="m-audit-card m-audit-card-daily-table">
                            <div className="m-audit-card-header m-header-with-badge">
                                <h4>Daily Audit Records</h4>
                                <span className="m-header-count-tag">{dailyBuckets.length} records</span>
                            </div>
                            <div className="m-audit-table-wrapper">
                                <table className="m-audit-table">
                                    <thead>
                                        <tr>
                                            <th>Operational Date</th>
                                            <th>Start Time ({timeBasis})</th>
                                            <th>End Time ({timeBasis})</th>
                                            <th>5-Min Buckets</th>
                                            <th>Complete</th>
                                            <th>Incomplete</th>
                                            <th>Samples</th>
                                            <th>Avg Eff. %</th>
                                            <th>Effective Eff. %</th>
                                            <th>Fan %</th>
                                            <th>Total Air Processed</th>
                                            <th>Total Air Cleaned</th>
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayedDailyBuckets.length === 0 ? (
                                            <tr>
                                                <td colSpan="14" className="m-table-empty-cell">
                                                    {loading ? 'Loading daily audit data...' : 'No daily audit records found for selected date range'}
                                                </td>
                                            </tr>
                                        ) : (
                                            displayedDailyBuckets.map((d, idx) => (
                                                <tr key={idx}>
                                                    <td className="m-cell-date-highlight">{d.operational_date || '--'}</td>
                                                    <td>{d.start_time ? d.start_time.replace('T', ' ') : '--'}</td>
                                                    <td>{d.end_time ? d.end_time.replace('T', ' ') : '--'}</td>
                                                    <td>{d.five_minute_bucket_count ?? '--'}</td>
                                                    <td><span className="m-text-success">{d.complete_bucket_count ?? '--'}</span></td>
                                                    <td>
                                                        <span className={d.incomplete_bucket_count > 0 ? 'm-text-danger' : 'm-text-muted'}>
                                                            {d.incomplete_bucket_count ?? 0}
                                                        </span>
                                                    </td>
                                                    <td>{d.sample_count ?? '--'}</td>
                                                    <td>{d.average_efficiency_percentage != null ? `${Number(d.average_efficiency_percentage).toFixed(1)}%` : '--'}</td>
                                                    <td>{d.effective_efficiency_percentage != null ? `${Number(d.effective_efficiency_percentage).toFixed(1)}%` : '--'}</td>
                                                    <td>{d.fan_percentage != null ? `${Math.round(d.fan_percentage)}%` : '--'}</td>
                                                    <td>{d.total_air_processed != null ? `${Math.round(d.total_air_processed).toLocaleString()} cu ft` : '--'}</td>
                                                    <td>{d.total_air_cleaned != null ? `${Math.round(d.total_air_cleaned).toLocaleString()} cu ft` : '--'}</td>
                                                    <td>
                                                        <span className={`m-badge m-badge-${(d.status || '--').toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                                                            {d.status || '--'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <button
                                                            type="button"
                                                            className="m-table-action-icon"
                                                            title="Inspect Day"
                                                            onClick={() => setSelectedDetail({ type: 'daily', item: d })}
                                                        >
                                                            <Eye size={14} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {totalDailyPages > 1 && (
                                <div className="m-audit-pagination-bar">
                                    <span className="m-pagination-info">
                                        Showing {(dailyPage - 1) * pageSizeDaily + 1} - {Math.min(dailyPage * pageSizeDaily, dailyBuckets.length)} of {dailyBuckets.length}
                                    </span>
                                    <div className="m-pagination-btns">
                                        <button
                                            type="button"
                                            className="m-audit-page-btn"
                                            disabled={dailyPage === 1}
                                            onClick={() => setDailyPage(p => Math.max(1, p - 1))}
                                        >
                                            <ChevronLeft size={14} />
                                        </button>
                                        <span className="m-page-indicator">{dailyPage} / {totalDailyPages}</span>
                                        <button
                                            type="button"
                                            className="m-audit-page-btn"
                                            disabled={dailyPage === totalDailyPages}
                                            onClick={() => setDailyPage(p => Math.min(totalDailyPages, p + 1))}
                                        >
                                            <ChevronRight size={14} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Daily Status Distribution */}
                        <div className="m-audit-card m-audit-card-daily-dist">
                            <div className="m-audit-card-header">
                                <h4>Daily Status Distribution</h4>
                            </div>
                            <div className="m-audit-table-wrapper">
                                <table className="m-audit-table m-audit-table-dist">
                                    <thead>
                                        <tr>
                                            <th>Status</th>
                                            <th>Count</th>
                                            <th>Percentage</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(() => {
                                            const { items, totalCount } = getStatusDistributionRows(dailyStatusDist);
                                            if (items.length === 0) {
                                                return (
                                                    <tr>
                                                        <td colSpan="3" className="m-table-empty-cell">
                                                            No status distribution data available
                                                        </td>
                                                    </tr>
                                                );
                                            }
                                            return (
                                                <>
                                                    {items.map((item, index) => {
                                                        const sName = item.status || '--';
                                                        const sType = sName.toLowerCase().replace(/[^a-z0-9]/g, '-');
                                                        return (
                                                            <tr key={index}>
                                                                <td>
                                                                    <div className="m-status-cell">
                                                                        <span className={`m-status-dot dot-${sType}`} />
                                                                        <span>{sName}</span>
                                                                    </div>
                                                                </td>
                                                                <td>{item.bucket_count ?? 0}</td>
                                                                <td>{item.bucket_percent != null ? `${Number(item.bucket_percent).toFixed(1)}%` : (totalCount > 0 ? `${Math.round((item.bucket_count / totalCount) * 100)}%` : '--')}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                    <tr className="m-row-total">
                                                        <td><strong>Total</strong></td>
                                                        <td><strong>{totalCount}</strong></td>
                                                        <td><strong>{totalCount > 0 ? '100%' : '--'}</strong></td>
                                                    </tr>
                                                </>
                                            );
                                        })()}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 6. SCREEN 4: ISSUES TAB */}
            {activeSubTab === 'Issues' && (
                <div className="m-audit-content">
                    <div className="m-audit-grid-row m-audit-row-top">
                        {/* Issue Types Summary */}
                        <div className="m-audit-card">
                            <div className="m-audit-card-header">
                                <h4>Categorized Issue Types</h4>
                            </div>
                            <div className="m-audit-table-wrapper">
                                <table className="m-audit-table">
                                    <thead>
                                        <tr>
                                            <th>Issue Code / Type</th>
                                            <th>Total Occurrences</th>
                                            <th>Last Recorded Datetime</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {topIssueTypes.length === 0 ? (
                                            <tr>
                                                <td colSpan="3" className="m-table-empty-cell">
                                                    {loading ? 'Loading issue types...' : 'No issue types detected for selected date range'}
                                                </td>
                                            </tr>
                                        ) : (
                                            topIssueTypes.map((issue, idx) => (
                                                <tr key={idx}>
                                                    <td className="m-cell-issuetype">
                                                        <strong>{formatIssueTypeName(issue.issue_type || issue.type)}</strong>
                                                    </td>
                                                    <td><span className="m-badge m-badge-error">{issue.issue_count ?? issue.count ?? 1}</span></td>
                                                    <td>{issue.last_occurrence_datetime ? issue.last_occurrence_datetime.replace('T', ' ') : '--'}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Recent Issues List */}
                        <div className="m-audit-card">
                            <div className="m-audit-card-header m-header-with-badge">
                                <h4>Detailed Issue Event Log</h4>
                                <span className="m-header-count-tag">{recentIssues.length} issues</span>
                            </div>
                            <div className="m-audit-table-wrapper">
                                <table className="m-audit-table">
                                    <thead>
                                        <tr>
                                            <th>Bucket Window ({timeBasis})</th>
                                            <th>Issue Name</th>
                                            <th>Severity</th>
                                            <th>Message</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayedRecentIssues.length === 0 ? (
                                            <tr>
                                                <td colSpan="5" className="m-table-empty-cell">
                                                    {loading ? 'Loading issue events...' : 'No issue events logged for selected date range'}
                                                </td>
                                            </tr>
                                        ) : (
                                            displayedRecentIssues.map((issue, idx) => {
                                                const times = getIssueTimes(issue);
                                                const sev = getIssueSeverity(issue);
                                                const msg = getIssueMessage(issue);
                                                const rawStart = issue.bucket_start_date || issue.start_time || issue.last_occurrence_datetime || issue.start;
                                                const datePart = rawStart ? (rawStart.substring(0, 10)) : '';
                                                return (
                                                    <tr key={idx}>
                                                        <td>{datePart ? `${datePart} ` : ''}{times.start} - {times.end}</td>
                                                        <td className="m-cell-issuetype"><strong>{formatIssueTypeName(issue.issue_type || issue.type)}</strong></td>
                                                        <td><span className={`m-severity-badge severity-${sev.toLowerCase()}`}>{sev}</span></td>
                                                        <td className="m-cell-message" title={msg}>{msg}</td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="m-table-action-icon"
                                                                title="Inspect Issue"
                                                                onClick={() => setSelectedDetail({ type: 'issue', item: issue })}
                                                            >
                                                                <Eye size={14} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {totalRecentIssuesPages > 1 && (
                                <div className="m-audit-pagination-bar">
                                    <span className="m-pagination-info">
                                        Showing {(recentIssuesPage - 1) * pageSizeRecentIssues + 1} - {Math.min(recentIssuesPage * pageSizeRecentIssues, recentIssues.length)} of {recentIssues.length}
                                    </span>
                                    <div className="m-pagination-btns">
                                        <button
                                            type="button"
                                            className="m-audit-page-btn"
                                            disabled={recentIssuesPage === 1}
                                            onClick={() => setRecentIssuesPage(p => Math.max(1, p - 1))}
                                        >
                                            <ChevronLeft size={14} />
                                        </button>
                                        <span className="m-page-indicator">{recentIssuesPage} / {totalRecentIssuesPages}</span>
                                        <button
                                            type="button"
                                            className="m-audit-page-btn"
                                            disabled={recentIssuesPage === totalRecentIssuesPages}
                                            onClick={() => setRecentIssuesPage(p => Math.min(totalRecentIssuesPages, p + 1))}
                                        >
                                            <ChevronRight size={14} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* 7. SCREEN 5: TELEMETRY TAB (24-Hour Telemetry Stream & Logs) */}
            {activeSubTab === 'Telemetry' && (
                <div className="m-audit-content m-telemetry-main-tab">
                    {/* Top Row: Real-time Telemetry KPI Overview Strip */}
                    <div className="m-audit-grid-row m-audit-row-top">
                        {/* 1. Telemetry Stream Status & Frequency Card */}
                        <div className="m-audit-card m-telemetry-kpi-card">
                            <div className="m-audit-card-header m-header-with-badge">
                                <h4>
                                    <Activity size={15} color="#E99924" style={{ marginRight: 6 }} />
                                    Telemetry Stream Overview
                                </h4>
                                <span className="m-header-count-tag">{telemetryRecords.length} records</span>
                            </div>
                            <div className="m-telemetry-overview-grid">
                                <div className="m-telemetry-kpi-item">
                                    <span className="m-telemetry-kpi-label">Device Status</span>
                                    <div className="m-telemetry-kpi-val-row">
                                        {latestTelemetry?.device_state ? (
                                            <span className={`m-health-badge health-${latestTelemetry.device_state.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                                                {latestTelemetry.device_state}
                                            </span>
                                        ) : null}
                                        {latestTelemetry?.device_health ? (
                                            <span className={`m-health-badge health-${latestTelemetry.device_health.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                                                {latestTelemetry.device_health}
                                            </span>
                                        ) : null}
                                        {!latestTelemetry?.device_state && !latestTelemetry?.device_health && (
                                            <span className="m-telemetry-kpi-val">--</span>
                                        )}
                                    </div>
                                </div>
                                <div className="m-telemetry-kpi-item">
                                    <span className="m-telemetry-kpi-label">Publish Interval</span>
                                    <span className="m-telemetry-kpi-val mono">
                                        {latestTelemetry?.publish_interval_sec != null ? `${latestTelemetry.publish_interval_sec}s` : '--'}
                                    </span>
                                </div>
                                <div className="m-telemetry-kpi-item">
                                    <span className="m-telemetry-kpi-label">Latest Packet ({timeBasis})</span>
                                    <span className="m-telemetry-kpi-val mono" title={latestTelemetry?.received_datetime || '--'}>
                                        {latestTelemetry ? formatTelemetryDateTime(latestTelemetry.received_datetime || latestTelemetry.received_utc) : '--'}
                                    </span>
                                </div>
                                <div className="m-telemetry-kpi-item">
                                    <span className="m-telemetry-kpi-label">Signal & Pressure</span>
                                    <span className="m-telemetry-kpi-val">
                                        {latestTelemetry?.signal_strength_dbm != null ? `${latestTelemetry.signal_strength_dbm} dBm` : '--'} | {latestTelemetry?.pressure_pa != null ? `${Number(latestTelemetry.pressure_pa).toFixed(1)} Pa` : '--'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* 2. Particulate Matter & Removal Efficiency KPI Card */}
                        <div className="m-audit-card m-telemetry-kpi-card">
                            <div className="m-audit-card-header">
                                <h4>
                                    <Wind size={15} color="#38bdf8" style={{ marginRight: 6 }} />
                                    PM Filtration & Efficiency
                                </h4>
                            </div>
                            <div className="m-telemetry-overview-grid">
                                <div className="m-telemetry-kpi-item">
                                    <span className="m-telemetry-kpi-label">Avg PM2.5 (In / Out)</span>
                                    <span className="m-telemetry-kpi-val">
                                        {avgTelemetryMetrics.pm25In != null ? Number(avgTelemetryMetrics.pm25In).toFixed(1) : '--'} / {avgTelemetryMetrics.pm25Out != null ? Number(avgTelemetryMetrics.pm25Out).toFixed(1) : '--'} µg/m³
                                    </span>
                                </div>
                                <div className="m-telemetry-kpi-item">
                                    <span className="m-telemetry-kpi-label">PM2.5 Removal Eff.</span>
                                    <span className="m-telemetry-kpi-val m-text-success">
                                        {avgTelemetryMetrics.pm25Eff != null ? `${Number(avgTelemetryMetrics.pm25Eff).toFixed(1)}%` : '--'}
                                    </span>
                                </div>
                                <div className="m-telemetry-kpi-item">
                                    <span className="m-telemetry-kpi-label">Avg PM10 (In / Out)</span>
                                    <span className="m-telemetry-kpi-val">
                                        {avgTelemetryMetrics.pm10In != null ? Number(avgTelemetryMetrics.pm10In).toFixed(1) : '--'} / {avgTelemetryMetrics.pm10Out != null ? Number(avgTelemetryMetrics.pm10Out).toFixed(1) : '--'} µg/m³
                                    </span>
                                </div>
                                <div className="m-telemetry-kpi-item">
                                    <span className="m-telemetry-kpi-label">PM10 Removal Eff.</span>
                                    <span className="m-telemetry-kpi-val m-text-success">
                                        {avgTelemetryMetrics.pm10Eff != null ? `${Number(avgTelemetryMetrics.pm10Eff).toFixed(1)}%` : '--'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* 3. Environment & CO2 KPI Card */}
                        <div className="m-audit-card m-telemetry-kpi-card">
                            <div className="m-audit-card-header">
                                <h4>
                                    <Thermometer size={15} color="#10b981" style={{ marginRight: 6 }} />
                                    Atmospheric & Fan Controls
                                </h4>
                            </div>
                            <div className="m-telemetry-overview-grid">
                                <div className="m-telemetry-kpi-item">
                                    <span className="m-telemetry-kpi-label">Avg Temp (In / Out)</span>
                                    <span className="m-telemetry-kpi-val">
                                        {avgTelemetryMetrics.tempIn != null ? `${Number(avgTelemetryMetrics.tempIn).toFixed(1)}°C` : '--'} / {avgTelemetryMetrics.tempOut != null ? `${Number(avgTelemetryMetrics.tempOut).toFixed(1)}°C` : '--'}
                                    </span>
                                </div>
                                <div className="m-telemetry-kpi-item">
                                    <span className="m-telemetry-kpi-label">Avg Humidity (In / Out)</span>
                                    <span className="m-telemetry-kpi-val">
                                        {avgTelemetryMetrics.humIn != null ? `${Number(avgTelemetryMetrics.humIn).toFixed(1)}%` : '--'} / {avgTelemetryMetrics.humOut != null ? `${Number(avgTelemetryMetrics.humOut).toFixed(1)}%` : '--'}
                                    </span>
                                </div>
                                <div className="m-telemetry-kpi-item">
                                    <span className="m-telemetry-kpi-label">Avg CO2 (In / Out)</span>
                                    <span className="m-telemetry-kpi-val">
                                        {avgTelemetryMetrics.co2In != null ? `${Math.round(avgTelemetryMetrics.co2In)}` : '--'} / {avgTelemetryMetrics.co2Out != null ? `${Math.round(avgTelemetryMetrics.co2Out)}` : '--'} ppm
                                    </span>
                                </div>
                                <div className="m-telemetry-kpi-item">
                                    <span className="m-telemetry-kpi-label">Fan Speed & Mode</span>
                                    <span className="m-telemetry-kpi-val">
                                        {latestTelemetry ? (
                                            <>
                                                {latestTelemetry.fan_speed_in_percent != null ? `${Math.round(latestTelemetry.fan_speed_in_percent)}%` : (latestTelemetry.fan_percentage != null ? `${Math.round(latestTelemetry.fan_percentage)}%` : '--')} In / {latestTelemetry.fan_speed_out_percent != null ? `${Math.round(latestTelemetry.fan_speed_out_percent)}%` : '--'} Out
                                                {latestTelemetry.fan_mode ? ` (${latestTelemetry.fan_mode})` : ''}
                                            </>
                                        ) : '--'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Middle Controls Strip: Category View Filter & Search */}
                    <div className="m-telemetry-controls-strip">
                        <div className="m-telemetry-category-pills">
                            <span className="m-telemetry-filter-label">
                                <Filter size={13} />
                                View:
                            </span>
                            <button
                                type="button"
                                className={`m-category-pill ${telemetryCategory === 'all' ? 'active' : ''}`}
                                onClick={() => setTelemetryCategory('all')}
                            >
                                All Sensor Streams
                            </button>
                            <button
                                type="button"
                                className={`m-category-pill ${telemetryCategory === 'pm' ? 'active' : ''}`}
                                onClick={() => setTelemetryCategory('pm')}
                            >
                                Particulate Matter (PM)
                            </button>
                            <button
                                type="button"
                                className={`m-category-pill ${telemetryCategory === 'env' ? 'active' : ''}`}
                                onClick={() => setTelemetryCategory('env')}
                            >
                                Environment & CO2
                            </button>
                            <button
                                type="button"
                                className={`m-category-pill ${telemetryCategory === 'particles' ? 'active' : ''}`}
                                onClick={() => setTelemetryCategory('particles')}
                            >
                                Particle Counts (0.5 - 10µm)
                            </button>
                            <button
                                type="button"
                                className={`m-category-pill ${telemetryCategory === 'diagnostics' ? 'active' : ''}`}
                                onClick={() => setTelemetryCategory('diagnostics')}
                            >
                                Diagnostics & Fan
                            </button>
                        </div>

                        <div className="m-telemetry-search-box">
                            <Search size={13} className="m-search-icon" />
                            <input
                                type="text"
                                placeholder="Search Telemetry ID, MQTT ID, Health, State..."
                                value={telemetrySearch}
                                onChange={(e) => {
                                    setTelemetrySearch(e.target.value);
                                    setTelemetryPage(1);
                                }}
                                className="m-telemetry-search-input"
                            />
                            {telemetrySearch && (
                                <button
                                    type="button"
                                    className="m-search-clear-btn"
                                    onClick={() => {
                                        setTelemetrySearch('');
                                        setTelemetryPage(1);
                                    }}
                                >
                                    ✕
                                </button>
                            )}
                        </div>

                        <div className="m-telemetry-page-size-selector">
                            <span className="m-size-label">Show:</span>
                            <select
                                value={telemetryPageSize}
                                onChange={(e) => {
                                    setTelemetryPageSize(Number(e.target.value));
                                    setTelemetryPage(1);
                                }}
                                className="m-telemetry-select"
                            >
                                <option value={10}>10 / page</option>
                                <option value={20}>20 / page</option>
                                <option value={50}>50 / page</option>
                                <option value={100}>100 / page</option>
                            </select>
                        </div>
                    </div>

                    {/* Telemetry Stream Records Table Card */}
                    <div className="m-audit-card m-telemetry-table-card">
                        <div className="m-audit-card-header m-header-with-badge">
                            <div className="m-header-title-group">
                                <h4>24-Hour Telemetry Stream & Sensor Logs</h4>
                                <span className="m-header-subtitle">
                                    Pole: <span className="mono">{poleId}</span> | Window: <span className="mono">{startDate} → {endDate}</span>
                                </span>
                            </div>
                            <div className="m-header-actions-group">
                                <span className="m-header-count-tag">
                                    {filteredTelemetry.length} {filteredTelemetry.length === 1 ? 'sample' : 'samples'}
                                </span>
                                <button
                                    type="button"
                                    className="m-telemetry-reload-btn"
                                    onClick={() => loadTelemetryData(true)}
                                    disabled={telemetryLoading}
                                    title="Reload telemetry records"
                                >
                                    <RefreshCw size={12} className={telemetryLoading ? 'spin-icon' : ''} />
                                    <span>Reload Stream</span>
                                </button>
                                <button
                                    type="button"
                                    className="m-telemetry-action-btn"
                                    onClick={handleExportCSV}
                                    disabled={telemetryRecords.length === 0}
                                    title="Export telemetry records to CSV"
                                >
                                    <Download size={12} />
                                    <span>Export Stream CSV</span>
                                </button>
                            </div>
                        </div>

                        {/* Loading / Error / Table Content */}
                        {telemetryLoading ? (
                            <div className="m-telemetry-loading-state">
                                <div className="m-telemetry-spinner" />
                                <p>Loading 24-hour telemetry stream for Pole {poleId}...</p>
                            </div>
                        ) : telemetryError && telemetryRecords.length === 0 ? (
                            <div className="m-telemetry-error-state">
                                <AlertTriangle size={28} color="#f59e0b" />
                                <h4>Unable to Load Telemetry Stream</h4>
                                <p>{telemetryError}</p>
                                <button
                                    type="button"
                                    className="m-audit-search-btn"
                                    onClick={() => loadTelemetryData(false)}
                                >
                                    <RefreshCw size={13} style={{ marginRight: 6 }} />
                                    Retry Loading Telemetry
                                </button>
                            </div>
                        ) : (
                            <div className="m-audit-telemetry-table-wrapper">
                                <table className="m-telemetry-table">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Telemetry ID</th>
                                            <th>MQTT ID</th>
                                            <th>Received Time ({timeBasis})</th>
                                            {(telemetryCategory === 'all' || telemetryCategory === 'pm') && (
                                                <>
                                                    <th>PM1.0 (In / Out)</th>
                                                    <th>PM2.5 (In / Out)</th>
                                                    <th>PM4.0 (In / Out)</th>
                                                    <th>PM10 (In / Out)</th>
                                                    <th>PM2.5 Eff.</th>
                                                </>
                                            )}
                                            {(telemetryCategory === 'all' || telemetryCategory === 'env') && (
                                                <>
                                                    <th>Temp °C (In / Out)</th>
                                                    <th>Humidity % (In / Out)</th>
                                                    <th>CO2 ppm (In / Out)</th>
                                                </>
                                            )}
                                            {(telemetryCategory === 'all' || telemetryCategory === 'particles') && (
                                                <>
                                                    <th>Part. 0.5 (In / Out)</th>
                                                    <th>Part. 1.0 (In / Out)</th>
                                                    <th>Part. 2.5 (In / Out)</th>
                                                    <th>Part. 10 (In / Out)</th>
                                                </>
                                            )}
                                            {(telemetryCategory === 'all' || telemetryCategory === 'diagnostics') && (
                                                <>
                                                    <th>Fan Speed (In / Out)</th>
                                                    <th>Mode</th>
                                                    <th>Pressure</th>
                                                    <th>Health & State</th>
                                                    <th>Signal</th>
                                                </>
                                            )}
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayedTelemetry.length === 0 ? (
                                            <tr>
                                                <td colSpan="18" className="m-table-empty-cell">
                                                    {telemetrySearch ? 'No telemetry records matching search keyword.' : 'No telemetry records found for selected date range.'}
                                                </td>
                                            </tr>
                                        ) : (
                                            displayedTelemetry.map((t, tIdx) => {
                                                const tId = t.sensor_telemetry_id ?? t.id ?? ((telemetryPage - 1) * telemetryPageSize + tIdx);
                                                const isExpanded = expandedMainTelemetryId === tId;
                                                const overallIndex = (telemetryPage - 1) * telemetryPageSize + tIdx + 1;

                                                const pm25Removal = (t.inlet_pm2_5_ug_m3 != null && t.outlet_pm2_5_ug_m3 != null && t.inlet_pm2_5_ug_m3 > 0)
                                                    ? (((t.inlet_pm2_5_ug_m3 - t.outlet_pm2_5_ug_m3) / t.inlet_pm2_5_ug_m3) * 100)
                                                    : null;

                                                return (
                                                    <React.Fragment key={tId}>
                                                        <tr
                                                            className={`m-telemetry-row ${isExpanded ? 'expanded' : ''}`}
                                                            onClick={() => setExpandedMainTelemetryId(prev => prev === tId ? null : tId)}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <td>{overallIndex}</td>
                                                            <td className="m-telemetry-id-cell">{t.sensor_telemetry_id ?? t.id ?? '--'}</td>
                                                            <td className="m-telemetry-mono-cell">{t.mqtt_message_id ?? '--'}</td>
                                                            <td className="m-telemetry-time-cell">
                                                                {formatTelemetryDateTime(t.received_datetime || t.received_utc || t.created_utc)}
                                                            </td>

                                                            {(telemetryCategory === 'all' || telemetryCategory === 'pm') && (
                                                                <>
                                                                    <td className="m-telemetry-metric-cell">
                                                                        {t.inlet_pm1_0_ug_m3 != null ? Number(t.inlet_pm1_0_ug_m3).toFixed(1) : '--'} / {t.outlet_pm1_0_ug_m3 != null ? Number(t.outlet_pm1_0_ug_m3).toFixed(1) : '--'}
                                                                    </td>
                                                                    <td className="m-telemetry-metric-cell">
                                                                        {t.inlet_pm2_5_ug_m3 != null ? Number(t.inlet_pm2_5_ug_m3).toFixed(1) : '--'} / {t.outlet_pm2_5_ug_m3 != null ? Number(t.outlet_pm2_5_ug_m3).toFixed(1) : '--'}
                                                                    </td>
                                                                    <td className="m-telemetry-metric-cell">
                                                                        {t.inlet_pm4_0_ug_m3 != null ? Number(t.inlet_pm4_0_ug_m3).toFixed(1) : '--'} / {t.outlet_pm4_0_ug_m3 != null ? Number(t.outlet_pm4_0_ug_m3).toFixed(1) : '--'}
                                                                    </td>
                                                                    <td className="m-telemetry-metric-cell">
                                                                        {t.inlet_pm10_ug_m3 != null ? Number(t.inlet_pm10_ug_m3).toFixed(1) : '--'} / {t.outlet_pm10_ug_m3 != null ? Number(t.outlet_pm10_ug_m3).toFixed(1) : '--'}
                                                                    </td>
                                                                    <td>
                                                                        {pm25Removal != null ? (
                                                                            <span className={`m-telemetry-eff-badge ${pm25Removal >= 65 ? 'good' : (pm25Removal >= 40 ? 'watch' : 'error')}`}>
                                                                                {pm25Removal.toFixed(1)}%
                                                                            </span>
                                                                        ) : '--'}
                                                                    </td>
                                                                </>
                                                            )}

                                                            {(telemetryCategory === 'all' || telemetryCategory === 'env') && (
                                                                <>
                                                                    <td>
                                                                        {t.inlet_temperature_c != null ? `${Number(t.inlet_temperature_c).toFixed(1)}°` : '--'} / {t.outlet_temperature_c != null ? `${Number(t.outlet_temperature_c).toFixed(1)}°` : '--'}
                                                                    </td>
                                                                    <td>
                                                                        {t.inlet_humidity_percent != null ? `${Number(t.inlet_humidity_percent).toFixed(1)}%` : '--'} / {t.outlet_humidity_percent != null ? `${Number(t.outlet_humidity_percent).toFixed(1)}%` : '--'}
                                                                    </td>
                                                                    <td>
                                                                        {t.inlet_co2_ppm != null ? `${Math.round(t.inlet_co2_ppm)}` : '--'} / {t.outlet_co2_ppm != null ? `${Math.round(t.outlet_co2_ppm)}` : '--'}
                                                                    </td>
                                                                </>
                                                            )}

                                                            {(telemetryCategory === 'all' || telemetryCategory === 'particles') && (
                                                                <>
                                                                    <td>{t.inlet_particlecount_0_5 != null ? Number(t.inlet_particlecount_0_5).toFixed(1) : '--'} / {t.outlet_particlecount_0_5 != null ? Number(t.outlet_particlecount_0_5).toFixed(1) : '--'}</td>
                                                                    <td>{t.inlet_particlecount_1_0 != null ? Number(t.inlet_particlecount_1_0).toFixed(1) : '--'} / {t.outlet_particlecount_1_0 != null ? Number(t.outlet_particlecount_1_0).toFixed(1) : '--'}</td>
                                                                    <td>{t.inlet_particlecount_2_5 != null ? Number(t.inlet_particlecount_2_5).toFixed(1) : '--'} / {t.outlet_particlecount_2_5 != null ? Number(t.outlet_particlecount_2_5).toFixed(1) : '--'}</td>
                                                                    <td>{t.inlet_particlecount_10 != null ? Number(t.inlet_particlecount_10).toFixed(1) : '--'} / {t.outlet_particlecount_10 != null ? Number(t.outlet_particlecount_10).toFixed(1) : '--'}</td>
                                                                </>
                                                            )}

                                                            {(telemetryCategory === 'all' || telemetryCategory === 'diagnostics') && (
                                                                <>
                                                                    <td>
                                                                        {t.fan_speed_in_percent != null ? `${Math.round(t.fan_speed_in_percent)}%` : (t.fan_percentage != null ? `${Math.round(t.fan_percentage)}%` : '--')} / {t.fan_speed_out_percent != null ? `${Math.round(t.fan_speed_out_percent)}%` : '--'}
                                                                    </td>
                                                                    <td>
                                                                        {t.fan_mode ? (
                                                                            <span className="m-telemetry-mode-tag">{t.fan_mode}</span>
                                                                        ) : '--'}
                                                                    </td>
                                                                    <td>{t.pressure_pa != null ? `${Number(t.pressure_pa).toFixed(1)} Pa` : '--'}</td>
                                                                    <td>
                                                                        {(t.device_health || t.device_state) ? (
                                                                            <span className={`m-health-badge health-${(t.device_health || t.device_state).toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                                                                                {t.device_health || t.device_state}
                                                                            </span>
                                                                        ) : '--'}
                                                                    </td>
                                                                    <td>{t.signal_strength_dbm != null ? `${t.signal_strength_dbm} dBm` : '--'}</td>
                                                                </>
                                                            )}

                                                            <td onClick={(e) => e.stopPropagation()}>
                                                                <div className="m-telemetry-action-buttons-wrap">
                                                                    <button
                                                                        type="button"
                                                                        className="m-telemetry-expand-btn"
                                                                        onClick={() => setExpandedMainTelemetryId(prev => prev === tId ? null : tId)}
                                                                        title={isExpanded ? 'Collapse Full Details' : 'Expand Full Details'}
                                                                    >
                                                                        {isExpanded ? '▲ Hide' : '▼ Details'}
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="m-table-action-icon"
                                                                        title="Inspect Telemetry Record"
                                                                        onClick={() => setSelectedDetail({ type: 'telemetry_single', item: t })}
                                                                    >
                                                                        <Eye size={13} />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>

                                                        {/* Full Record Expanded Row */}
                                                        {isExpanded && (
                                                            <tr className="m-telemetry-expanded-row">
                                                                <td colSpan="20">
                                                                    <div className="m-telemetry-full-detail-card">
                                                                        {/* Row 1: Particle Counts Breakdown */}
                                                                        <div className="m-telemetry-detail-group">
                                                                            <h5>
                                                                                <Layers size={13} style={{ marginRight: 5, verticalAlign: 'middle' }} />
                                                                                Particle Counts Breakdown (Inlet vs Outlet)
                                                                            </h5>
                                                                            <div className="m-telemetry-particles-grid">
                                                                                <div className="m-particle-chip">
                                                                                    <span className="p-label">0.5 µm:</span>
                                                                                    <span className="p-val">{t.inlet_particlecount_0_5 != null ? Number(t.inlet_particlecount_0_5).toFixed(2) : '--'} / {t.outlet_particlecount_0_5 != null ? Number(t.outlet_particlecount_0_5).toFixed(2) : '--'}</span>
                                                                                </div>
                                                                                <div className="m-particle-chip">
                                                                                    <span className="p-label">1.0 µm:</span>
                                                                                    <span className="p-val">{t.inlet_particlecount_1_0 != null ? Number(t.inlet_particlecount_1_0).toFixed(2) : '--'} / {t.outlet_particlecount_1_0 != null ? Number(t.outlet_particlecount_1_0).toFixed(2) : '--'}</span>
                                                                                </div>
                                                                                <div className="m-particle-chip">
                                                                                    <span className="p-label">2.5 µm:</span>
                                                                                    <span className="p-val">{t.inlet_particlecount_2_5 != null ? Number(t.inlet_particlecount_2_5).toFixed(2) : '--'} / {t.outlet_particlecount_2_5 != null ? Number(t.outlet_particlecount_2_5).toFixed(2) : '--'}</span>
                                                                                </div>
                                                                                <div className="m-particle-chip">
                                                                                    <span className="p-label">4.0 µm:</span>
                                                                                    <span className="p-val">{t.inlet_particlecount_4_0 != null ? Number(t.inlet_particlecount_4_0).toFixed(2) : '--'} / {t.outlet_particlecount_4_0 != null ? Number(t.outlet_particlecount_4_0).toFixed(2) : '--'}</span>
                                                                                </div>
                                                                                <div className="m-particle-chip">
                                                                                    <span className="p-label">10 µm:</span>
                                                                                    <span className="p-val">{t.inlet_particlecount_10 != null ? Number(t.inlet_particlecount_10).toFixed(2) : '--'} / {t.outlet_particlecount_10 != null ? Number(t.outlet_particlecount_10).toFixed(2) : '--'}</span>
                                                                                </div>
                                                                            </div>
                                                                        </div>

                                                                        {/* Row 2: Device & MQTT Metadata */}
                                                                        <div className="m-telemetry-detail-group">
                                                                            <h5>
                                                                                <Cpu size={13} style={{ marginRight: 5, verticalAlign: 'middle' }} />
                                                                                Device, Network & MQTT Parameters
                                                                            </h5>
                                                                            <div className="m-telemetry-meta-grid">
                                                                                <div className="m-meta-item">
                                                                                    <span className="m-meta-key">Device ID:</span>
                                                                                    <span className="m-meta-val mono">{t.device_id || poleId}</span>
                                                                                </div>
                                                                                <div className="m-meta-item">
                                                                                    <span className="m-meta-key">MQTT Message ID:</span>
                                                                                    <span className="m-meta-val mono">{t.mqtt_message_id ?? '--'}</span>
                                                                                </div>
                                                                                <div className="m-meta-item">
                                                                                    <span className="m-meta-key">Topic:</span>
                                                                                    <span className="m-meta-val mono">{t.topic || '--'}</span>
                                                                                </div>
                                                                                <div className="m-meta-item">
                                                                                    <span className="m-meta-key">Publish Interval:</span>
                                                                                    <span className="m-meta-val">{t.publish_interval_sec != null ? `${t.publish_interval_sec} seconds` : '--'}</span>
                                                                                </div>
                                                                                <div className="m-meta-item">
                                                                                    <span className="m-meta-key">Signal Strength:</span>
                                                                                    <span className="m-meta-val">{t.signal_strength_dbm != null ? `${t.signal_strength_dbm} dBm` : '--'}</span>
                                                                                </div>
                                                                                <div className="m-meta-item">
                                                                                    <span className="m-meta-key">Received Local:</span>
                                                                                    <span className="m-meta-val">{t.received_datetime || '--'}</span>
                                                                                </div>
                                                                                <div className="m-meta-item">
                                                                                    <span className="m-meta-key">Received UTC:</span>
                                                                                    <span className="m-meta-val">{t.received_utc || '--'}</span>
                                                                                </div>
                                                                                <div className="m-meta-item">
                                                                                    <span className="m-meta-key">Created UTC:</span>
                                                                                    <span className="m-meta-val">{t.created_utc || '--'}</span>
                                                                                </div>
                                                                                <div className="m-meta-item">
                                                                                    <span className="m-meta-key">Fan Speed (In / Out):</span>
                                                                                    <span className="m-meta-val">{t.fan_speed_in_percent != null ? `${t.fan_speed_in_percent}%` : '--'} In / {t.fan_speed_out_percent != null ? `${t.fan_speed_out_percent}%` : '--'} Out</span>
                                                                                </div>
                                                                                <div className="m-meta-item">
                                                                                    <span className="m-meta-key">Pressure Sensor:</span>
                                                                                    <span className="m-meta-val">{t.pressure_pa != null ? `${t.pressure_pa} Pa` : '--'}</span>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Pagination Bar */}
                        {!telemetryLoading && totalTelemetryPages > 1 && (
                            <div className="m-audit-pagination-bar">
                                <span className="m-pagination-info">
                                    Showing {(telemetryPage - 1) * telemetryPageSize + 1} - {Math.min(telemetryPage * telemetryPageSize, filteredTelemetry.length)} of {filteredTelemetry.length} samples
                                </span>
                                <div className="m-pagination-btns">
                                    <button
                                        type="button"
                                        className="m-audit-page-btn"
                                    >
                                        <ChevronRight size={14} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* 7. SCREEN 6: COMMANDS TAB */}
            {activeSubTab === 'Commands' && (
                <div className="m-audit-content">
                    {/* Top Row: Commands KPI Summary Strip */}
                    <div className="m-audit-telemetry-summary-strip m-commands-summary-strip">
                        <div className="m-telemetry-stat-box">
                            <span className="m-telemetry-stat-label">Total Dispatched</span>
                            <span className="m-telemetry-stat-value">
                                {commandsSummaryStats.totalCount} commands
                            </span>
                        </div>
                        <div className="m-telemetry-stat-box">
                            <span className="m-telemetry-stat-label">Unique Actions</span>
                            <span className="m-telemetry-stat-value">
                                {commandsSummaryStats.uniqueActionsCount}
                            </span>
                        </div>
                        <div className="m-telemetry-stat-box">
                            <span className="m-telemetry-stat-label">Avg Dispatch Latency</span>
                            <span className="m-telemetry-stat-value">
                                {commandsSummaryStats.avgLatencyMs != null ? (
                                    <span className="m-latency-badge">
                                        <Clock size={11} style={{ marginRight: 4 }} />
                                        {commandsSummaryStats.avgLatencyMs} ms
                                    </span>
                                ) : '--'}
                            </span>
                        </div>
                        <div className="m-telemetry-stat-box">
                            <span className="m-telemetry-stat-label">Latest Fan Setpoint</span>
                            <span className="m-telemetry-stat-value">
                                {commandsSummaryStats.latestFanIn != null ? (
                                    <span className="m-fan-speed-badge">
                                        <Wind size={11} style={{ marginRight: 4 }} />
                                        {commandsSummaryStats.latestFanIn}% In / {commandsSummaryStats.latestFanOut != null ? `${commandsSummaryStats.latestFanOut}%` : '--'} Out
                                    </span>
                                ) : '--'}
                            </span>
                        </div>
                        <div className="m-telemetry-stat-box">
                            <span className="m-telemetry-stat-label">Latest Command Sent</span>
                            <span className="m-telemetry-stat-value">
                                {commandsSummaryStats.latestCommand?.sent_datetime ? formatTelemetryDateTime(commandsSummaryStats.latestCommand.sent_datetime) : '--'}
                            </span>
                        </div>
                    </div>

                    {/* Middle Controls Strip: Action Filter Pills + Search Bar + Page Size + Export/Reload */}
                    <div className="m-telemetry-controls-strip">
                        <div className="m-telemetry-filter-pills">
                            <span className="m-telemetry-filter-label">
                                <Filter size={13} />
                                Action:
                            </span>
                            <button
                                type="button"
                                className={`m-category-pill ${commandsActionFilter === 'all' ? 'active' : ''}`}
                                onClick={() => {
                                    setCommandsActionFilter('all');
                                    setCommandsPage(1);
                                }}
                            >
                                <Sliders size={12} />
                                <span>All Actions</span>
                                <span className="pill-count">({commandRecords.length})</span>
                            </button>
                            {uniqueCommandActions.map(action => {
                                const count = commandRecords.filter(c => (c.action || c.command_name) === action).length;
                                return (
                                    <button
                                        key={action}
                                        type="button"
                                        className={`m-category-pill ${commandsActionFilter === action ? 'active' : ''}`}
                                        onClick={() => {
                                            setCommandsActionFilter(action);
                                            setCommandsPage(1);
                                        }}
                                    >
                                        <Terminal size={12} />
                                        <span>{formatCommandName(action)}</span>
                                        <span className="pill-count">({count})</span>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="m-telemetry-right-controls">
                            <div className="m-telemetry-search-box">
                                <Search size={13} className="m-search-icon" />
                                <input
                                    type="text"
                                    className="m-telemetry-search-input"
                                    placeholder="Search command, action, payload..."
                                    value={commandsSearch}
                                    onChange={(e) => {
                                        setCommandsSearch(e.target.value);
                                        setCommandsPage(1);
                                    }}
                                />
                                {commandsSearch && (
                                    <button
                                        type="button"
                                        className="m-search-clear-btn"
                                        onClick={() => {
                                            setCommandsSearch('');
                                            setCommandsPage(1);
                                        }}
                                    >
                                        <X size={12} />
                                    </button>
                                )}
                            </div>

                            <div className="m-telemetry-page-size-selector">
                                <span className="m-size-label">Show:</span>
                                <select
                                    className="m-telemetry-select"
                                    value={commandsPageSize}
                                    onChange={(e) => {
                                        setCommandsPageSize(Number(e.target.value));
                                        setCommandsPage(1);
                                    }}
                                >
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>

                            <button
                                type="button"
                                className="m-telemetry-action-btn"
                                onClick={handleExportCSV}
                                title="Export commands history to CSV"
                            >
                                <Download size={13} />
                                <span>Export CSV</span>
                            </button>

                            <button
                                type="button"
                                className="m-telemetry-reload-btn"
                                onClick={() => loadCommandsData(true)}
                                disabled={commandsLoading}
                                title="Reload Commands from Server"
                            >
                                <RefreshCw size={13} className={commandsLoading ? 'spin-icon' : ''} />
                                <span>Reload</span>
                            </button>
                        </div>
                    </div>

                    {/* Main Commands Table Card */}
                    <div className="m-audit-card m-commands-table-card">
                        <div className="m-audit-card-header m-header-with-badge">
                            <div className="m-header-title-group">
                                <h4>
                                    <Terminal size={15} color="#38bdf8" style={{ marginRight: 6 }} />
                                    Maintenance Commands Audit Log
                                </h4>
                                <span className="m-header-subtitle">
                                    Pole: <span className="mono">{poleId}</span> | Window: <span className="mono">{startDate} → {endDate}</span>
                                </span>
                            </div>
                            <div className="m-header-actions-group">
                                <span className="m-header-count-tag">{filteredCommands.length} of {commandRecords.length} records</span>
                            </div>
                        </div>

                        {/* Loading / Error / Table Content */}
                        {commandsLoading ? (
                            <div className="m-telemetry-loading-state">
                                <div className="m-telemetry-spinner" />
                                <p>Loading maintenance command records for Pole {poleId}...</p>
                            </div>
                        ) : commandsError && commandRecords.length === 0 ? (
                            <div className="m-telemetry-error-state">
                                <AlertTriangle size={28} color="#f59e0b" />
                                <h4>Unable to Load Maintenance Commands</h4>
                                <p>{commandsError}</p>
                                <button
                                    type="button"
                                    className="m-audit-search-btn"
                                    onClick={() => loadCommandsData(false)}
                                >
                                    <RefreshCw size={13} style={{ marginRight: 6 }} />
                                    Retry Loading Commands
                                </button>
                            </div>
                        ) : (
                            <div className="m-audit-telemetry-table-wrapper">
                                <table className="m-telemetry-table">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Command Name</th>
                                            <th>Action</th>
                                            <th>Created Time ({timeBasis})</th>
                                            <th>Sent Time ({timeBasis})</th>
                                            <th>Latency</th>
                                            <th>Mode</th>
                                            <th>Fan In / Out %</th>
                                            <th>Payload Preview</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayedCommands.length === 0 ? (
                                            <tr>
                                                <td colSpan="10" className="m-table-empty-cell">
                                                    {commandsSearch
                                                        ? 'No maintenance commands matching search keyword.'
                                                        : `No maintenance commands found for Pole ${poleId} in the selected date range (${startDate} to ${endDate}). Try picking a date range with executed commands (e.g. 01/09/2026 to 03/09/2026).`}
                                                </td>
                                            </tr>
                                        ) : (
                                            displayedCommands.map((cmd, cIdx) => {
                                                const cmdId = ((commandsPage - 1) * commandsPageSize + cIdx);
                                                const overallIndex = (commandsPage - 1) * commandsPageSize + cIdx + 1;
                                                const parsed = parseCommandPayload(cmd.payload_json);
                                                const cmdObj = parsed?.command || parsed || {};
                                                const latency = calculateLatency(cmd.created_datetime, cmd.sent_datetime);
                                                const payloadPreviewText = typeof cmd.payload_json === 'string'
                                                    ? cmd.payload_json
                                                    : JSON.stringify(cmd.payload_json || '');

                                                return (
                                                    <tr
                                                        key={cmdId}
                                                        className="m-telemetry-row"
                                                        onClick={() => setSelectedDetail({ type: 'command_single', item: cmd })}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <td>{overallIndex}</td>
                                                        <td>
                                                            <span className="m-command-name-cell">
                                                                <Terminal size={13} color="#E99924" />
                                                                <span>{formatCommandName(cmd.command_name)}</span>
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className="m-command-action-badge">
                                                                {cmd.action || cmd.command_name || '--'}
                                                            </span>
                                                        </td>
                                                        <td className="m-telemetry-time-cell">
                                                            {formatTelemetryDateTime(cmd.created_datetime)}
                                                        </td>
                                                        <td className="m-telemetry-time-cell">
                                                            {formatTelemetryDateTime(cmd.sent_datetime)}
                                                        </td>
                                                        <td>
                                                            {latency != null ? (
                                                                <span className="m-latency-badge" title={`Dispatched in ${latency} milliseconds`}>
                                                                    {latency} ms
                                                                </span>
                                                            ) : '--'}
                                                        </td>
                                                        <td>
                                                            {cmdObj.mode ? (
                                                                <span className="m-telemetry-mode-tag">
                                                                    {cmdObj.mode}
                                                                </span>
                                                            ) : '--'}
                                                        </td>
                                                        <td>
                                                            {cmdObj.fan_speed_in_percent != null ? (
                                                                <span className="m-fan-speed-badge">
                                                                    {cmdObj.fan_speed_in_percent}% In / {cmdObj.fan_speed_out_percent != null ? `${cmdObj.fan_speed_out_percent}%` : '--'} Out
                                                                </span>
                                                            ) : '--'}
                                                        </td>
                                                        <td>
                                                            <span
                                                                className="m-payload-preview-chip m-clickable-chip"
                                                                title="Click to view Raw JSON Payload in Table Format"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedDetail({ type: 'command_single', item: cmd });
                                                                }}
                                                            >
                                                                <Code size={10} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                                                                {payloadPreviewText}
                                                            </span>
                                                        </td>
                                                        <td onClick={(e) => e.stopPropagation()}>
                                                            <div className="m-telemetry-action-buttons-wrap">
                                                                <button
                                                                    type="button"
                                                                    className="m-telemetry-payload-btn"
                                                                    onClick={() => setSelectedDetail({ type: 'command_single', item: cmd })}
                                                                    title="Open Command Payload Table & Details Popup"
                                                                >
                                                                    <Code size={11} style={{ marginRight: 3 }} />
                                                                    <span>Payload</span>
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="m-table-action-icon"
                                                                    title="Inspect Command Detail"
                                                                    onClick={() => setSelectedDetail({ type: 'command_single', item: cmd })}
                                                                >
                                                                    <Eye size={13} />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Pagination Bar */}
                        {!commandsLoading && totalCommandsPages > 1 && (
                            <div className="m-audit-pagination-bar">
                                <span className="m-pagination-info">
                                    Showing {(commandsPage - 1) * commandsPageSize + 1} - {Math.min(commandsPage * commandsPageSize, filteredCommands.length)} of {filteredCommands.length} commands
                                </span>
                                <div className="m-pagination-btns">
                                    <button
                                        type="button"
                                        className="m-audit-page-btn"
                                        disabled={commandsPage === 1}
                                        onClick={() => setCommandsPage(p => Math.max(1, p - 1))}
                                    >
                                        <ChevronLeft size={14} />
                                    </button>
                                    <span className="m-page-indicator">{commandsPage} / {totalCommandsPages}</span>
                                    <button
                                        type="button"
                                        className="m-audit-page-btn"
                                        disabled={commandsPage === totalCommandsPages}
                                        onClick={() => setCommandsPage(p => Math.min(totalCommandsPages, p + 1))}
                                    >
                                        <ChevronRight size={14} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* 8. INSPECT DETAIL MODAL */}
            {selectedDetail && (
                <div className="m-audit-detail-overlay" onClick={() => setSelectedDetail(null)}>
                    <div
                        className={`m-audit-detail-modal ${(selectedDetail.type === '5min' || selectedDetail.type === 'telemetry_single' || selectedDetail.type === 'command_single') ? 'm-audit-modal-wide' : ''}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="m-audit-detail-header">
                            <h3>
                                {selectedDetail.type === '5min' && `5-Min Bucket Telemetry Stream (${formatTimeDisplay(selectedDetail.item.start_time)} - ${formatTimeDisplay(selectedDetail.item.end_time)})`}
                                {selectedDetail.type === 'telemetry_single' && `Sensor Telemetry Record #${selectedDetail.item.sensor_telemetry_id ?? selectedDetail.item.id ?? ''} (${formatTelemetryDateTime(selectedDetail.item.received_datetime || selectedDetail.item.received_utc)})`}
                                {selectedDetail.type === 'command_single' && `Maintenance Command: ${formatCommandName(selectedDetail.item.command_name)} (${formatTelemetryDateTime(selectedDetail.item.sent_datetime || selectedDetail.item.created_datetime)})`}
                                {selectedDetail.type === 'hourly' && `Hourly Audit Record (${formatTimeDisplay(selectedDetail.item.start_time)} - ${formatTimeDisplay(selectedDetail.item.end_time)})`}
                                {selectedDetail.type === 'daily' && `Daily Audit Record (${selectedDetail.item.operational_date || 'Date'})`}
                                {selectedDetail.type === 'issue' && `Issue Detail: ${formatIssueTypeName(selectedDetail.item.issue_type || selectedDetail.item.type)}`}
                            </h3>
                            <button className="m-audit-close-btn" onClick={() => setSelectedDetail(null)}>✕</button>
                        </div>
                        <div className="m-audit-detail-body">
                            {selectedDetail.type === 'telemetry_single' && (
                                <div className="m-audit-telemetry-modal-content">
                                    <div className="m-audit-telemetry-summary-strip">
                                        <div className="m-telemetry-stat-box">
                                            <span className="m-telemetry-stat-label">Telemetry ID</span>
                                            <span className="m-telemetry-stat-value mono">
                                                {selectedDetail.item.sensor_telemetry_id ?? selectedDetail.item.id ?? '--'}
                                            </span>
                                        </div>
                                        <div className="m-telemetry-stat-box">
                                            <span className="m-telemetry-stat-label">MQTT Msg ID</span>
                                            <span className="m-telemetry-stat-value mono">
                                                {selectedDetail.item.mqtt_message_id ?? '--'}
                                            </span>
                                        </div>
                                        <div className="m-telemetry-stat-box">
                                            <span className="m-telemetry-stat-label">Received Time ({timeBasis})</span>
                                            <span className="m-telemetry-stat-value">
                                                {formatTelemetryDateTime(selectedDetail.item.received_datetime || selectedDetail.item.received_utc || selectedDetail.item.created_utc)}
                                            </span>
                                        </div>
                                        <div className="m-telemetry-stat-box">
                                            <span className="m-telemetry-stat-label">PM2.5 (In / Out)</span>
                                            <span className="m-telemetry-stat-value">
                                                {selectedDetail.item.inlet_pm2_5_ug_m3 != null ? Number(selectedDetail.item.inlet_pm2_5_ug_m3).toFixed(1) : '--'} / {selectedDetail.item.outlet_pm2_5_ug_m3 != null ? Number(selectedDetail.item.outlet_pm2_5_ug_m3).toFixed(1) : '--'} µg/m³
                                            </span>
                                        </div>
                                        <div className="m-telemetry-stat-box">
                                            <span className="m-telemetry-stat-label">PM10 (In / Out)</span>
                                            <span className="m-telemetry-stat-value">
                                                {selectedDetail.item.inlet_pm10_ug_m3 != null ? Number(selectedDetail.item.inlet_pm10_ug_m3).toFixed(1) : '--'} / {selectedDetail.item.outlet_pm10_ug_m3 != null ? Number(selectedDetail.item.outlet_pm10_ug_m3).toFixed(1) : '--'} µg/m³
                                            </span>
                                        </div>
                                        <div className="m-telemetry-stat-box">
                                            <span className="m-telemetry-stat-label">Health & State</span>
                                            <span className="m-telemetry-stat-value">
                                                {selectedDetail.item.device_health || selectedDetail.item.device_state ? (
                                                    <span className={`m-health-badge health-${(selectedDetail.item.device_health || selectedDetail.item.device_state).toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                                                        {selectedDetail.item.device_health || selectedDetail.item.device_state}
                                                    </span>
                                                ) : (
                                                    <span className="m-telemetry-mono-cell">--</span>
                                                )}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="m-telemetry-full-detail-card">
                                        <div className="m-telemetry-detail-group">
                                            <h5>Particle Counts Breakdown (Inlet vs Outlet)</h5>
                                            <div className="m-telemetry-particles-grid">
                                                <div className="m-particle-chip"><span className="p-label">0.5 µm:</span><span className="p-val">{selectedDetail.item.inlet_particlecount_0_5 != null ? Number(selectedDetail.item.inlet_particlecount_0_5).toFixed(2) : '--'} / {selectedDetail.item.outlet_particlecount_0_5 != null ? Number(selectedDetail.item.outlet_particlecount_0_5).toFixed(2) : '--'}</span></div>
                                                <div className="m-particle-chip"><span className="p-label">1.0 µm:</span><span className="p-val">{selectedDetail.item.inlet_particlecount_1_0 != null ? Number(selectedDetail.item.inlet_particlecount_1_0).toFixed(2) : '--'} / {selectedDetail.item.outlet_particlecount_1_0 != null ? Number(selectedDetail.item.outlet_particlecount_1_0).toFixed(2) : '--'}</span></div>
                                                <div className="m-particle-chip"><span className="p-label">2.5 µm:</span><span className="p-val">{selectedDetail.item.inlet_particlecount_2_5 != null ? Number(selectedDetail.item.inlet_particlecount_2_5).toFixed(2) : '--'} / {selectedDetail.item.outlet_particlecount_2_5 != null ? Number(selectedDetail.item.outlet_particlecount_2_5).toFixed(2) : '--'}</span></div>
                                                <div className="m-particle-chip"><span className="p-label">4.0 µm:</span><span className="p-val">{selectedDetail.item.inlet_particlecount_4_0 != null ? Number(selectedDetail.item.inlet_particlecount_4_0).toFixed(2) : '--'} / {selectedDetail.item.outlet_particlecount_4_0 != null ? Number(selectedDetail.item.outlet_particlecount_4_0).toFixed(2) : '--'}</span></div>
                                                <div className="m-particle-chip"><span className="p-label">10 µm:</span><span className="p-val">{selectedDetail.item.inlet_particlecount_10 != null ? Number(selectedDetail.item.inlet_particlecount_10).toFixed(2) : '--'} / {selectedDetail.item.outlet_particlecount_10 != null ? Number(selectedDetail.item.outlet_particlecount_10).toFixed(2) : '--'}</span></div>
                                            </div>
                                        </div>

                                        <div className="m-telemetry-detail-group">
                                            <h5>Device, Communication & Environmental Parameters</h5>
                                            <div className="m-telemetry-meta-grid">
                                                <div className="m-meta-item"><span className="m-meta-key">Device ID:</span><span className="m-meta-val mono">{selectedDetail.item.device_id || poleId}</span></div>
                                                <div className="m-meta-item"><span className="m-meta-key">Topic:</span><span className="m-meta-val mono">{selectedDetail.item.topic || (poleId ? `${poleId}/telemetry` : '--')}</span></div>
                                                <div className="m-meta-item"><span className="m-meta-key">Temperature (In / Out):</span><span className="m-meta-val">{selectedDetail.item.inlet_temperature_c != null ? `${Number(selectedDetail.item.inlet_temperature_c).toFixed(1)}°C` : '--'} / {selectedDetail.item.outlet_temperature_c != null ? `${Number(selectedDetail.item.outlet_temperature_c).toFixed(1)}°C` : '--'}</span></div>
                                                <div className="m-meta-item"><span className="m-meta-key">Humidity (In / Out):</span><span className="m-meta-val">{selectedDetail.item.inlet_humidity_percent != null ? `${Number(selectedDetail.item.inlet_humidity_percent).toFixed(1)}%` : '--'} / {selectedDetail.item.outlet_humidity_percent != null ? `${Number(selectedDetail.item.outlet_humidity_percent).toFixed(1)}%` : '--'}</span></div>
                                                <div className="m-meta-item"><span className="m-meta-key">CO2 (In / Out):</span><span className="m-meta-val">{selectedDetail.item.inlet_co2_ppm != null ? `${Math.round(selectedDetail.item.inlet_co2_ppm)} ppm` : '--'} / {selectedDetail.item.outlet_co2_ppm != null ? `${Math.round(selectedDetail.item.outlet_co2_ppm)} ppm` : '--'}</span></div>
                                                <div className="m-meta-item"><span className="m-meta-key">Fan Speed (In / Out):</span><span className="m-meta-val">{selectedDetail.item.fan_speed_in_percent != null ? `${selectedDetail.item.fan_speed_in_percent}%` : '--'} In / {selectedDetail.item.fan_speed_out_percent != null ? `${selectedDetail.item.fan_speed_out_percent}%` : '--'} Out{selectedDetail.item.fan_mode ? ` (${selectedDetail.item.fan_mode})` : ''}</span></div>
                                                <div className="m-meta-item"><span className="m-meta-key">Pressure:</span><span className="m-meta-val">{selectedDetail.item.pressure_pa != null ? `${selectedDetail.item.pressure_pa} Pa` : '--'}</span></div>
                                                <div className="m-meta-item"><span className="m-meta-key">Signal Strength:</span><span className="m-meta-val">{selectedDetail.item.signal_strength_dbm != null ? `${selectedDetail.item.signal_strength_dbm} dBm` : '--'}</span></div>
                                                <div className="m-meta-item"><span className="m-meta-key">Publish Interval:</span><span className="m-meta-val">{selectedDetail.item.publish_interval_sec != null ? `${selectedDetail.item.publish_interval_sec}s` : '--'}</span></div>
                                                <div className="m-meta-item"><span className="m-meta-key">Received Local:</span><span className="m-meta-val">{selectedDetail.item.received_datetime || '--'}</span></div>
                                                <div className="m-meta-item"><span className="m-meta-key">Received UTC:</span><span className="m-meta-val">{selectedDetail.item.received_utc || '--'}</span></div>
                                                <div className="m-meta-item"><span className="m-meta-key">Created UTC:</span><span className="m-meta-val">{selectedDetail.item.created_utc || '--'}</span></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {selectedDetail.type === 'command_single' && (() => {
                                const parsed = parseCommandPayload(selectedDetail.item.payload_json);
                                const cmdObj = parsed?.command || parsed || {};
                                const latency = calculateLatency(selectedDetail.item.created_datetime, selectedDetail.item.sent_datetime);
                                const payloadRows = flattenJsonPayload(selectedDetail.item.payload_json);
                                const filteredRows = payloadSearch
                                    ? payloadRows.filter(r =>
                                        r.path.toLowerCase().includes(payloadSearch.toLowerCase()) ||
                                        String(r.displayValue).toLowerCase().includes(payloadSearch.toLowerCase()) ||
                                        r.type.toLowerCase().includes(payloadSearch.toLowerCase())
                                    )
                                    : payloadRows;

                                return (
                                    <div className="m-audit-telemetry-modal-content">
                                        {/* Command Overview KPI Strip */}
                                        <div className="m-audit-telemetry-summary-strip">
                                            <div className="m-telemetry-stat-box">
                                                <span className="m-telemetry-stat-label">Command Name</span>
                                                <span className="m-telemetry-stat-value">
                                                    {formatCommandName(selectedDetail.item.command_name)}
                                                </span>
                                            </div>
                                            <div className="m-telemetry-stat-box">
                                                <span className="m-telemetry-stat-label">Action</span>
                                                <span className="m-telemetry-stat-value mono">
                                                    {selectedDetail.item.action || '--'}
                                                </span>
                                            </div>
                                            <div className="m-telemetry-stat-box">
                                                <span className="m-telemetry-stat-label">Mode</span>
                                                <span className="m-telemetry-stat-value">
                                                    {cmdObj.mode ? (
                                                        <span className="m-telemetry-mode-tag">{cmdObj.mode}</span>
                                                    ) : (
                                                        <span className="m-telemetry-mono-cell">--</span>
                                                    )}
                                                </span>
                                            </div>
                                            <div className="m-telemetry-stat-box">
                                                <span className="m-telemetry-stat-label">Latency</span>
                                                <span className="m-telemetry-stat-value">
                                                    {latency != null ? `${latency} ms` : '--'}
                                                </span>
                                            </div>
                                            <div className="m-telemetry-stat-box">
                                                <span className="m-telemetry-stat-label">Fan In / Out</span>
                                                <span className="m-telemetry-stat-value">
                                                    {cmdObj.fan_speed_in_percent != null || cmdObj.fan_speed_out_percent != null ? (
                                                        `${cmdObj.fan_speed_in_percent != null ? `${cmdObj.fan_speed_in_percent}%` : '--'} In / ${cmdObj.fan_speed_out_percent != null ? `${cmdObj.fan_speed_out_percent}%` : '--'} Out`
                                                    ) : '--'}
                                                </span>
                                            </div>
                                            <div className="m-telemetry-stat-box">
                                                <span className="m-telemetry-stat-label">Sent Datetime ({timeBasis})</span>
                                                <span className="m-telemetry-stat-value">
                                                    {formatTelemetryDateTime(selectedDetail.item.sent_datetime)}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="m-telemetry-full-detail-card">
                                            {/* Execution Parameters Grid */}
                                            <div className="m-telemetry-detail-group">
                                                <h5>
                                                    <Sliders size={13} style={{ marginRight: 5, verticalAlign: 'middle' }} />
                                                    Command Execution Parameters & Metadata
                                                </h5>
                                                <div className="m-telemetry-meta-grid">
                                                    <div className="m-meta-item"><span className="m-meta-key">Pole ID:</span><span className="m-meta-val mono">{poleId}</span></div>
                                                    <div className="m-meta-item"><span className="m-meta-key">Command Name:</span><span className="m-meta-val mono">{selectedDetail.item.command_name || '--'}</span></div>
                                                    <div className="m-meta-item"><span className="m-meta-key">Action:</span><span className="m-meta-val mono">{selectedDetail.item.action || '--'}</span></div>
                                                    <div className="m-meta-item"><span className="m-meta-key">Mode:</span><span className="m-meta-val">{cmdObj.mode || '--'}</span></div>
                                                    <div className="m-meta-item"><span className="m-meta-key">Fan Speed In:</span><span className="m-meta-val">{cmdObj.fan_speed_in_percent != null ? `${cmdObj.fan_speed_in_percent}%` : '--'}</span></div>
                                                    <div className="m-meta-item"><span className="m-meta-key">Fan Speed Out:</span><span className="m-meta-val">{cmdObj.fan_speed_out_percent != null ? `${cmdObj.fan_speed_out_percent}%` : '--'}</span></div>
                                                    <div className="m-meta-item"><span className="m-meta-key">Created Datetime:</span><span className="m-meta-val">{selectedDetail.item.created_datetime ? selectedDetail.item.created_datetime.replace('T', ' ') : '--'}</span></div>
                                                    <div className="m-meta-item"><span className="m-meta-key">Sent Datetime:</span><span className="m-meta-val">{selectedDetail.item.sent_datetime ? selectedDetail.item.sent_datetime.replace('T', ' ') : '--'}</span></div>
                                                    <div className="m-meta-item"><span className="m-meta-key">Dispatch Latency:</span><span className="m-meta-val">{latency != null ? `${latency} ms` : '--'}</span></div>
                                                </div>
                                            </div>

                                            {/* Raw JSON Payload in Table Format */}
                                            <div className="m-telemetry-detail-group">
                                                <div className="m-payload-section-header">
                                                    <div className="m-payload-title-group">
                                                        <h5>
                                                            <Code size={14} color="#38bdf8" style={{ marginRight: 6, verticalAlign: 'middle' }} />
                                                            Raw JSON Payload
                                                        </h5>
                                                        <span className="m-payload-count-tag">
                                                            {payloadRows.length} parameter{payloadRows.length === 1 ? '' : 's'}
                                                        </span>
                                                    </div>

                                                    <div className="m-payload-controls-group">
                                                        {/* Toggle Table vs Raw Code */}
                                                        <div className="m-payload-view-toggle">
                                                            <button
                                                                type="button"
                                                                className={`m-toggle-btn ${payloadViewMode === 'table' ? 'active' : ''}`}
                                                                onClick={() => setPayloadViewMode('table')}
                                                                title="View as structured Table"
                                                            >
                                                                <Table size={12} style={{ marginRight: 4 }} />
                                                                <span>Table</span>
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className={`m-toggle-btn ${payloadViewMode === 'raw' ? 'active' : ''}`}
                                                                onClick={() => setPayloadViewMode('raw')}
                                                                title="View as raw JSON text"
                                                            >
                                                                <Code size={12} style={{ marginRight: 4 }} />
                                                                <span>JSON</span>
                                                            </button>
                                                        </div>

                                                        {/* Copy JSON Button */}
                                                        <button
                                                            type="button"
                                                            className="m-copy-json-btn"
                                                            onClick={() => handleCopyPayloadJson(selectedDetail.item.payload_json, 'modal')}
                                                            title="Copy Raw JSON to Clipboard"
                                                        >
                                                            {copiedCommandId === 'modal' ? (
                                                                <>
                                                                    <Check size={12} color="#10b981" />
                                                                    <span>Copied!</span>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Copy size={12} />
                                                                    <span>Copy JSON</span>
                                                                </>
                                                            )}
                                                        </button>
                                                    </div>
                                                </div>

                                                {payloadViewMode === 'table' ? (
                                                    <div className="m-payload-table-box">
                                                        {payloadRows.length > 5 && (
                                                            <div className="m-payload-search-bar">
                                                                <Search size={12} color="#8a99ad" />
                                                                <input
                                                                    type="text"
                                                                    placeholder="Filter payload parameters or values..."
                                                                    value={payloadSearch}
                                                                    onChange={(e) => setPayloadSearch(e.target.value)}
                                                                    className="m-payload-search-input"
                                                                />
                                                                {payloadSearch && (
                                                                    <button
                                                                        type="button"
                                                                        className="m-search-clear-btn"
                                                                        onClick={() => setPayloadSearch('')}
                                                                    >
                                                                        <X size={10} />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}

                                                        <div className="m-payload-table-wrapper">
                                                            <table className="m-payload-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th style={{ width: '40px' }}>#</th>
                                                                        <th style={{ width: '36%' }}>Parameter / Key Path</th>
                                                                        <th style={{ width: '16%' }}>Data Type</th>
                                                                        <th>Value</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {filteredRows.length === 0 ? (
                                                                        <tr>
                                                                            <td colSpan="4" className="m-table-empty-cell">
                                                                                {payloadSearch
                                                                                    ? 'No matching payload keys found.'
                                                                                    : 'No payload data available.'}
                                                                            </td>
                                                                        </tr>
                                                                    ) : (
                                                                        filteredRows.map((row, rIdx) => (
                                                                            <tr key={rIdx}>
                                                                                <td className="m-payload-idx-cell">{rIdx + 1}</td>
                                                                                <td className="m-payload-key-cell">
                                                                                    <span className="m-payload-key-badge mono">
                                                                                        {row.path}
                                                                                    </span>
                                                                                </td>
                                                                                <td>
                                                                                    <span className={`m-payload-type-tag type-${row.type}`}>
                                                                                        {row.type}
                                                                                    </span>
                                                                                </td>
                                                                                <td className="m-payload-value-cell">
                                                                                    {row.type === 'boolean' ? (
                                                                                        <span className={`m-bool-pill ${row.value ? 'bool-true' : 'bool-false'}`}>
                                                                                            {String(row.value)}
                                                                                        </span>
                                                                                    ) : row.type === 'number' ? (
                                                                                        <span className="m-num-pill mono">
                                                                                            {row.displayValue}
                                                                                        </span>
                                                                                    ) : row.type === 'null' ? (
                                                                                        <span className="m-null-pill mono">null</span>
                                                                                    ) : (
                                                                                        <span className="m-str-val mono">
                                                                                            {row.displayValue}
                                                                                        </span>
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                        ))
                                                                    )}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="m-json-viewer-box">
                                                        <pre className="m-json-pre">
                                                            {parsed ? JSON.stringify(parsed, null, 2) : (selectedDetail.item.payload_json || '{}')}
                                                        </pre>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}

                            {selectedDetail.type === '5min' && (
                                <div className="m-audit-telemetry-modal-content">
                                    {/* Bucket Summary Overview Strip */}
                                    <div className="m-audit-telemetry-summary-strip">
                                        <div className="m-telemetry-stat-box">
                                            <span className="m-telemetry-stat-label">Bucket Window ({timeBasis})</span>
                                            <span className="m-telemetry-stat-value">
                                                {formatTimeDisplay(selectedDetail.item.start_time)} - {formatTimeDisplay(selectedDetail.item.end_time)}
                                            </span>
                                        </div>
                                        <div className="m-telemetry-stat-box">
                                            <span className="m-telemetry-stat-label">Telemetry ID Range</span>
                                            <span className="m-telemetry-stat-value mono">
                                                {selectedDetail.item.starting_telemetry_id ?? '--'} → {selectedDetail.item.ending_telemetry_id ?? '--'}
                                            </span>
                                        </div>
                                        <div className="m-telemetry-stat-box">
                                            <span className="m-telemetry-stat-label">Efficiency</span>
                                            <span className="m-telemetry-stat-value">
                                                {selectedDetail.item.efficiency_percentage != null ? `${Number(selectedDetail.item.efficiency_percentage).toFixed(2)}%` : '--'}
                                            </span>
                                        </div>
                                        <div className="m-telemetry-stat-box">
                                            <span className="m-telemetry-stat-label">PM10 (In / Out)</span>
                                            <span className="m-telemetry-stat-value">
                                                {selectedDetail.item.pm10_inlet != null ? Number(selectedDetail.item.pm10_inlet).toFixed(1) : '--'} / {selectedDetail.item.pm10_outlet != null ? Number(selectedDetail.item.pm10_outlet).toFixed(1) : '--'} µg/m³
                                            </span>
                                        </div>
                                        <div className="m-telemetry-stat-box">
                                            <span className="m-telemetry-stat-label">PM2.5 (In / Out)</span>
                                            <span className="m-telemetry-stat-value">
                                                {selectedDetail.item.pm25_inlet != null ? Number(selectedDetail.item.pm25_inlet).toFixed(1) : '--'} / {selectedDetail.item.pm25_outlet != null ? Number(selectedDetail.item.pm25_outlet).toFixed(1) : '--'} µg/m³
                                            </span>
                                        </div>
                                        <div className="m-telemetry-stat-box">
                                            <span className="m-telemetry-stat-label">Fan Speed</span>
                                            <span className="m-telemetry-stat-value">
                                                {selectedDetail.item.fan_percentage != null ? `${Math.round(selectedDetail.item.fan_percentage)}%` : '--'}
                                            </span>
                                        </div>
                                        <div className="m-telemetry-stat-box">
                                            <span className="m-telemetry-stat-label">Bucket Status</span>
                                            <span className="m-telemetry-stat-value">
                                                {selectedDetail.item.status ? (
                                                    <span className={`m-badge m-badge-${selectedDetail.item.status.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                                                        {selectedDetail.item.status}
                                                    </span>
                                                ) : (
                                                    <span className="m-badge m-badge---">--</span>
                                                )}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Telemetry Stream Section Header */}
                                    <div className="m-telemetry-section-header">
                                        <div className="m-telemetry-section-title">
                                            <Activity size={15} color="#E99924" />
                                            <span>Sensor Telemetry Records (Full Stream)</span>
                                            {!bucketTelemetryLoading && bucketTelemetryList.length > 0 && (
                                                <span className="m-telemetry-count-badge">{bucketTelemetryList.length} records</span>
                                            )}
                                        </div>
                                        <div className="m-telemetry-header-actions">
                                            {!bucketTelemetryLoading && bucketTelemetryList.length > 0 && (
                                                <button
                                                    type="button"
                                                    className="m-telemetry-action-btn"
                                                    onClick={handleExportBucketTelemetryCSV}
                                                    title="Export all telemetry records to CSV"
                                                >
                                                    <Download size={13} />
                                                    <span>Export CSV</span>
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className="m-telemetry-reload-btn"
                                                onClick={() => handleInspect5MinBucket(selectedDetail.item)}
                                                disabled={bucketTelemetryLoading}
                                                title="Reload Telemetry Records"
                                            >
                                                <RefreshCw size={13} className={bucketTelemetryLoading ? 'spin-icon' : ''} />
                                                <span>Reload</span>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Telemetry Records Table or States */}
                                    {bucketTelemetryLoading ? (
                                        <div className="m-telemetry-loading-state">
                                            <div className="m-telemetry-spinner" />
                                            <p>Loading telemetry records (IDs: {selectedDetail.item.starting_telemetry_id ?? '--'} to {selectedDetail.item.ending_telemetry_id ?? '--'})...</p>
                                        </div>
                                    ) : bucketTelemetryError && bucketTelemetryList.length === 0 ? (
                                        <div className="m-telemetry-error-state">
                                            <AlertTriangle size={24} color="#f59e0b" />
                                            <p>{bucketTelemetryError}</p>
                                            <button
                                                type="button"
                                                className="m-audit-search-btn"
                                                onClick={() => handleInspect5MinBucket(selectedDetail.item)}
                                            >
                                                Retry Fetch
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="m-audit-telemetry-table-wrapper">
                                            <table className="m-telemetry-table">
                                                <thead>
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Telemetry ID</th>
                                                        <th>MQTT ID</th>
                                                        <th>Received Time ({timeBasis})</th>
                                                        <th>PM1.0 (In / Out)</th>
                                                        <th>PM2.5 (In / Out)</th>
                                                        <th>PM4.0 (In / Out)</th>
                                                        <th>PM10 (In / Out)</th>
                                                        <th>Temp °C (In / Out)</th>
                                                        <th>Humidity % (In / Out)</th>
                                                        <th>CO2 ppm (In / Out)</th>
                                                        <th>Part. 0.5 (In / Out)</th>
                                                        <th>Part. 2.5 (In / Out)</th>
                                                        <th>Part. 10 (In / Out)</th>
                                                        <th>Fan In / Out</th>
                                                        <th>Fan Mode</th>
                                                        <th>Pressure</th>
                                                        <th>Health / State</th>
                                                        <th>Signal</th>
                                                        <th>Details</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {bucketTelemetryList.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="20" className="m-table-empty-cell">
                                                                No telemetry samples found for this bucket window.
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        bucketTelemetryList.map((t, tIdx) => {
                                                            const tId = t.sensor_telemetry_id ?? t.id ?? tIdx;
                                                            const isExpanded = expandedTelemetryId === tId;

                                                            return (
                                                                <React.Fragment key={tId}>
                                                                    <tr
                                                                        className={`m-telemetry-row ${isExpanded ? 'expanded' : ''}`}
                                                                        onClick={() => toggleExpandTelemetry(tId)}
                                                                        style={{ cursor: 'pointer' }}
                                                                    >
                                                                        <td>{tIdx + 1}</td>
                                                                        <td className="m-telemetry-id-cell">{t.sensor_telemetry_id ?? t.id ?? '--'}</td>
                                                                        <td className="m-telemetry-mono-cell">{t.mqtt_message_id ?? '--'}</td>
                                                                        <td className="m-telemetry-time-cell">
                                                                            {formatTelemetryDateTime(t.received_datetime || t.received_utc || t.created_utc)}
                                                                        </td>
                                                                        <td className="m-telemetry-metric-cell">
                                                                            {t.inlet_pm1_0_ug_m3 != null ? Number(t.inlet_pm1_0_ug_m3).toFixed(1) : '--'} / {t.outlet_pm1_0_ug_m3 != null ? Number(t.outlet_pm1_0_ug_m3).toFixed(1) : '--'}
                                                                        </td>
                                                                        <td className="m-telemetry-metric-cell">
                                                                            {t.inlet_pm2_5_ug_m3 != null ? Number(t.inlet_pm2_5_ug_m3).toFixed(1) : '--'} / {t.outlet_pm2_5_ug_m3 != null ? Number(t.outlet_pm2_5_ug_m3).toFixed(1) : '--'}
                                                                        </td>
                                                                        <td className="m-telemetry-metric-cell">
                                                                            {t.inlet_pm4_0_ug_m3 != null ? Number(t.inlet_pm4_0_ug_m3).toFixed(1) : '--'} / {t.outlet_pm4_0_ug_m3 != null ? Number(t.outlet_pm4_0_ug_m3).toFixed(1) : '--'}
                                                                        </td>
                                                                        <td className="m-telemetry-metric-cell">
                                                                            {t.inlet_pm10_ug_m3 != null ? Number(t.inlet_pm10_ug_m3).toFixed(1) : '--'} / {t.outlet_pm10_ug_m3 != null ? Number(t.outlet_pm10_ug_m3).toFixed(1) : '--'}
                                                                        </td>
                                                                        <td>
                                                                            {t.inlet_temperature_c != null ? `${Number(t.inlet_temperature_c).toFixed(1)}°` : '--'} / {t.outlet_temperature_c != null ? `${Number(t.outlet_temperature_c).toFixed(1)}°` : '--'}
                                                                        </td>
                                                                        <td>
                                                                            {t.inlet_humidity_percent != null ? `${Number(t.inlet_humidity_percent).toFixed(1)}%` : '--'} / {t.outlet_humidity_percent != null ? `${Number(t.outlet_humidity_percent).toFixed(1)}%` : '--'}
                                                                        </td>
                                                                        <td>
                                                                            {t.inlet_co2_ppm != null ? `${Math.round(t.inlet_co2_ppm)}` : '--'} / {t.outlet_co2_ppm != null ? `${Math.round(t.outlet_co2_ppm)}` : '--'}
                                                                        </td>
                                                                        <td>
                                                                            {t.inlet_particlecount_0_5 != null ? Number(t.inlet_particlecount_0_5).toFixed(1) : '--'} / {t.outlet_particlecount_0_5 != null ? Number(t.outlet_particlecount_0_5).toFixed(1) : '--'}
                                                                        </td>
                                                                        <td>
                                                                            {t.inlet_particlecount_2_5 != null ? Number(t.inlet_particlecount_2_5).toFixed(1) : '--'} / {t.outlet_particlecount_2_5 != null ? Number(t.outlet_particlecount_2_5).toFixed(1) : '--'}
                                                                        </td>
                                                                        <td>
                                                                            {t.inlet_particlecount_10 != null ? Number(t.inlet_particlecount_10).toFixed(1) : '--'} / {t.outlet_particlecount_10 != null ? Number(t.outlet_particlecount_10).toFixed(1) : '--'}
                                                                        </td>
                                                                        <td>
                                                                            {t.fan_speed_in_percent != null ? `${Math.round(t.fan_speed_in_percent)}%` : (t.fan_percentage != null ? `${Math.round(t.fan_percentage)}%` : '--')} / {t.fan_speed_out_percent != null ? `${Math.round(t.fan_speed_out_percent)}%` : '--'}
                                                                        </td>
                                                                        <td>
                                                                            {t.fan_mode ? <span className="m-telemetry-mode-tag">{t.fan_mode}</span> : <span className="m-telemetry-mono-cell">--</span>}
                                                                        </td>
                                                                        <td>{t.pressure_pa != null ? `${Number(t.pressure_pa).toFixed(1)} Pa` : '--'}</td>
                                                                        <td>
                                                                            {t.device_health || t.device_state ? (
                                                                                <span className={`m-health-badge health-${(t.device_health || t.device_state).toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                                                                                    {t.device_health || t.device_state}
                                                                                </span>
                                                                            ) : (
                                                                                <span className="m-telemetry-mono-cell">--</span>
                                                                            )}
                                                                        </td>
                                                                        <td>{t.signal_strength_dbm != null ? `${t.signal_strength_dbm} dBm` : '--'}</td>
                                                                        <td>
                                                                            <button
                                                                                type="button"
                                                                                className="m-telemetry-expand-btn"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    toggleExpandTelemetry(tId);
                                                                                }}
                                                                                title={isExpanded ? 'Collapse Full Details' : 'Expand Full Record Details'}
                                                                            >
                                                                                {isExpanded ? '▲ Hide' : '▼ View All'}
                                                                            </button>
                                                                        </td>
                                                                    </tr>

                                                                    {/* Full Record Expanded Card */}
                                                                    {isExpanded && (
                                                                        <tr className="m-telemetry-expanded-row">
                                                                            <td colSpan="20">
                                                                                <div className="m-telemetry-full-detail-card">
                                                                                    {/* Row 1: Particle Counts Full Breakdown */}
                                                                                    <div className="m-telemetry-detail-group">
                                                                                        <h5>Particle Counts Breakdown (Inlet vs Outlet)</h5>
                                                                                        <div className="m-telemetry-particles-grid">
                                                                                            <div className="m-particle-chip">
                                                                                                <span className="p-label">0.5 µm:</span>
                                                                                                <span className="p-val">{t.inlet_particlecount_0_5 != null ? Number(t.inlet_particlecount_0_5).toFixed(2) : '--'} / {t.outlet_particlecount_0_5 != null ? Number(t.outlet_particlecount_0_5).toFixed(2) : '--'}</span>
                                                                                            </div>
                                                                                            <div className="m-particle-chip">
                                                                                                <span className="p-label">1.0 µm:</span>
                                                                                                <span className="p-val">{t.inlet_particlecount_1_0 != null ? Number(t.inlet_particlecount_1_0).toFixed(2) : '--'} / {t.outlet_particlecount_1_0 != null ? Number(t.outlet_particlecount_1_0).toFixed(2) : '--'}</span>
                                                                                            </div>
                                                                                            <div className="m-particle-chip">
                                                                                                <span className="p-label">2.5 µm:</span>
                                                                                                <span className="p-val">{t.inlet_particlecount_2_5 != null ? Number(t.inlet_particlecount_2_5).toFixed(2) : '--'} / {t.outlet_particlecount_2_5 != null ? Number(t.outlet_particlecount_2_5).toFixed(2) : '--'}</span>
                                                                                            </div>
                                                                                            <div className="m-particle-chip">
                                                                                                <span className="p-label">4.0 µm:</span>
                                                                                                <span className="p-val">{t.inlet_particlecount_4_0 != null ? Number(t.inlet_particlecount_4_0).toFixed(2) : '--'} / {t.outlet_particlecount_4_0 != null ? Number(t.outlet_particlecount_4_0).toFixed(2) : '--'}</span>
                                                                                            </div>
                                                                                            <div className="m-particle-chip">
                                                                                                <span className="p-label">10 µm:</span>
                                                                                                <span className="p-val">{t.inlet_particlecount_10 != null ? Number(t.inlet_particlecount_10).toFixed(2) : '--'} / {t.outlet_particlecount_10 != null ? Number(t.outlet_particlecount_10).toFixed(2) : '--'}</span>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>

                                                                                    {/* Row 2: Device & Communication Metadata */}
                                                                                    <div className="m-telemetry-detail-group">
                                                                                        <h5>Device & MQTT Telemetry Parameters</h5>
                                                                                        <div className="m-telemetry-meta-grid">
                                                                                            <div className="m-meta-item">
                                                                                                <span className="m-meta-key">Device ID:</span>
                                                                                                <span className="m-meta-val">{t.device_id || poleId}</span>
                                                                                            </div>
                                                                                            <div className="m-meta-item">
                                                                                                <span className="m-meta-key">Publish Interval:</span>
                                                                                                <span className="m-meta-val">{t.publish_interval_sec != null ? `${t.publish_interval_sec} seconds` : '--'}</span>
                                                                                            </div>
                                                                                            <div className="m-meta-item">
                                                                                                <span className="m-meta-key">Signal Strength:</span>
                                                                                                <span className="m-meta-val">{t.signal_strength_dbm != null ? `${t.signal_strength_dbm} dBm` : '--'}</span>
                                                                                            </div>
                                                                                            <div className="m-meta-item">
                                                                                                <span className="m-meta-key">Received Local:</span>
                                                                                                <span className="m-meta-val">{t.received_datetime || '--'}</span>
                                                                                            </div>
                                                                                            <div className="m-meta-item">
                                                                                                <span className="m-meta-key">Received UTC:</span>
                                                                                                <span className="m-meta-val">{t.received_utc || '--'}</span>
                                                                                            </div>
                                                                                            <div className="m-meta-item">
                                                                                                <span className="m-meta-key">Created UTC:</span>
                                                                                                <span className="m-meta-val">{t.created_utc || '--'}</span>
                                                                                            </div>
                                                                                            <div className="m-meta-item">
                                                                                                <span className="m-meta-key">Fan Speed Out:</span>
                                                                                                <span className="m-meta-val">{t.fan_speed_out_percent != null ? `${t.fan_speed_out_percent}%` : '--'}</span>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    )}
                                                                </React.Fragment>
                                                            );
                                                        })
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            )}

                            {selectedDetail.type === 'hourly' && (
                                <>
                                    <div className="m-detail-row">
                                        <span>Complete / Incomplete Buckets:</span>
                                        <strong>{selectedDetail.item.complete_bucket_count ?? '--'} / {selectedDetail.item.incomplete_bucket_count ?? '--'}</strong>
                                    </div>
                                    <div className="m-detail-row">
                                        <span>Sample Count:</span>
                                        <strong>{selectedDetail.item.sample_count ?? '--'}</strong>
                                    </div>
                                    <div className="m-detail-row">
                                        <span>Average Efficiency:</span>
                                        <strong>{selectedDetail.item.average_efficiency_percentage != null ? `${Number(selectedDetail.item.average_efficiency_percentage).toFixed(2)}%` : '--'}</strong>
                                    </div>
                                    <div className="m-detail-row">
                                        <span>Effective Efficiency:</span>
                                        <strong>{selectedDetail.item.effective_efficiency_percentage != null ? `${Number(selectedDetail.item.effective_efficiency_percentage).toFixed(2)}%` : '--'}</strong>
                                    </div>
                                    <div className="m-detail-row">
                                        <span>Total Air Processed:</span>
                                        <strong>{selectedDetail.item.total_air_processed != null ? `${Math.round(selectedDetail.item.total_air_processed).toLocaleString()} cu ft` : '--'}</strong>
                                    </div>
                                    <div className="m-detail-row">
                                        <span>Total Air Cleaned:</span>
                                        <strong>{selectedDetail.item.total_air_cleaned != null ? `${Math.round(selectedDetail.item.total_air_cleaned).toLocaleString()} cu ft` : '--'}</strong>
                                    </div>
                                </>
                            )}

                            {selectedDetail.type === 'daily' && (
                                <>
                                    <div className="m-detail-row">
                                        <span>Operational Date:</span>
                                        <strong>{selectedDetail.item.operational_date || '--'}</strong>
                                    </div>
                                    <div className="m-detail-row">
                                        <span>Complete 5-Min Buckets:</span>
                                        <strong>{selectedDetail.item.complete_bucket_count ?? '--'} / {selectedDetail.item.five_minute_bucket_count ?? '--'}</strong>
                                    </div>
                                    <div className="m-detail-row">
                                        <span>Total Samples:</span>
                                        <strong>{selectedDetail.item.sample_count ?? '--'}</strong>
                                    </div>
                                    <div className="m-detail-row">
                                        <span>Average Efficiency:</span>
                                        <strong>{selectedDetail.item.average_efficiency_percentage != null ? `${Number(selectedDetail.item.average_efficiency_percentage).toFixed(2)}%` : '--'}</strong>
                                    </div>
                                    <div className="m-detail-row">
                                        <span>Total Air Cleaned:</span>
                                        <strong>{selectedDetail.item.total_air_cleaned != null ? `${Math.round(selectedDetail.item.total_air_cleaned).toLocaleString()} cu ft` : '--'}</strong>
                                    </div>
                                </>
                            )}

                            {selectedDetail.type === 'issue' && (
                                <>
                                    <div className="m-detail-row">
                                        <span>Issue Type:</span>
                                        <strong>{formatIssueTypeName(selectedDetail.item.issue_type || selectedDetail.item.type)}</strong>
                                    </div>
                                    <div className="m-detail-row">
                                        <span>Severity:</span>
                                        <span className={`m-severity-badge severity-${getIssueSeverity(selectedDetail.item).toLowerCase()}`}>
                                            {getIssueSeverity(selectedDetail.item)}
                                        </span>
                                    </div>
                                    <div className="m-detail-row">
                                        <span>Bucket Window:</span>
                                        <strong>
                                            {(selectedDetail.item.bucket_start_date || selectedDetail.item.start_time || selectedDetail.item.start || '--').replace('T', ' ')}
                                            {' - '}
                                            {(selectedDetail.item.bucket_end_date || selectedDetail.item.end_time || selectedDetail.item.end || '--').replace('T', ' ')}
                                        </strong>
                                    </div>
                                    <div className="m-detail-row">
                                        <span>Message:</span>
                                        <strong>{getIssueMessage(selectedDetail.item)}</strong>
                                    </div>
                                    {selectedDetail.item.last_occurrence_datetime && (
                                        <div className="m-detail-row">
                                            <span>Last Occurrence:</span>
                                            <strong>{selectedDetail.item.last_occurrence_datetime.replace('T', ' ')}</strong>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MaintenanceAuditPanel;
